import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from './ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CurrencyInput } from './ui/currency-input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SearchableSelect } from './ui/searchable-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import {
  User,
  FileText,
  Users,
  CreditCard,
  Gift,
  Building2,
  Plus,
  Minus,
  Upload,
  Search,
  Calculator,
  MapPin,
  Navigation,
  Clock,
  Route,
  AlertCircle,
  GraduationCap,
  BookOpen,
  CheckCircle2,
  CircleX,
  Award,
  Target,
  TrendingUp,
  Briefcase,
  Heart,
  Key,
  Loader2,
  Smartphone,
  Check,
  X
} from 'lucide-react';
import FeedbackHistory from './feedback/FeedbackHistory';
import DCFEmployeeTab from './dcf/DCFEmployeeTab';
import { formatCPF, formatRG, formatPhone, formatCEP, onlyNumbers, validateCPF, validatePhone, validateCEP, validateDateBR } from '../lib/helpers';
import DocumentosFuncionarioSection from './DocumentosFuncionarioSection';

const API = process.env.REACT_APP_BACKEND_URL;

const BENEFICIOS_DEPENDENTE = [
  { value: 'seguro_vida', label: 'Seguro de vida' },
  { value: 'convenio_medico', label: 'Convênio médico' },
  { value: 'convenio_odontologico', label: 'Convênio odontológico' },
  { value: 'outro', label: 'Outro benefício' },
];

const COMPETENCIAS_BENEFICIO_DEPENDENTE = [
  { value: 'folha_mensal', label: 'Folha mensal' },
  { value: 'adiantamento', label: 'Adiantamento' },
  { value: '13_primeira', label: '13º 1ª parcela' },
  { value: '13_segunda', label: '13º 2ª parcela' },
  { value: 'ferias', label: 'Férias' },
  { value: 'rescisao', label: 'Rescisão' },
];

const formatDependentBenefitCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return '';
  return (Number(digits) / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const parseDependentBenefitCurrency = (value) => {
  if (typeof value === 'number') return value;
  const digits = String(value || '').replace(/\D/g, '');
  return digits ? Number(digits) / 100 : 0;
};

// Helper function to format date without timezone issues
// When parsing "YYYY-MM-DD" strings, this avoids UTC conversion problems
const formatDateBR = (dateStr) => {
  if (!dateStr) return 'Não informado';
  // If it's already in a date format, extract the date part
  const cleanDate = dateStr.split('T')[0];
  const parts = cleanDate.split('-');
  if (parts.length === 3) {
    // Return in DD/MM/YYYY format without Date object conversion
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

// Helper to calculate age from date string without timezone issues
const calculateAge = (dateStr) => {
  if (!dateStr) return null;
  const cleanDate = dateStr.split('T')[0];
  const parts = cleanDate.split('-');
  if (parts.length !== 3) return null;

  const birthYear = parseInt(parts[0], 10);
  const birthMonth = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
  const birthDay = parseInt(parts[2], 10);

  const today = new Date();
  let age = today.getFullYear() - birthYear;
  const monthDiff = today.getMonth() - birthMonth;

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDay)) {
    age--;
  }

  return age;
};

// Helper to calculate company tenure from date string without timezone issues
const calculateTenure = (dateStr) => {
  if (!dateStr) return '';
  const cleanDate = dateStr.split('T')[0];
  const parts = cleanDate.split('-');
  if (parts.length !== 3) return '';

  const admissaoYear = parseInt(parts[0], 10);
  const admissaoMonth = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
  const admissaoDay = parseInt(parts[2], 10);

  const hoje = new Date();
  let totalMeses = (hoje.getFullYear() - admissaoYear) * 12 + (hoje.getMonth() - admissaoMonth);
  if (hoje.getDate() < admissaoDay) {
    totalMeses--;
  }
  totalMeses = Math.max(totalMeses, 0);

  if (totalMeses < 12) {
    return `${totalMeses} ${totalMeses === 1 ? 'mês' : 'meses'} na empresa`;
  }
  const anosCompletos = Math.floor(totalMeses / 12);
  const mesesRestantes = totalMeses % 12;
  return mesesRestantes > 0
    ? `${anosCompletos} ${anosCompletos === 1 ? 'ano' : 'anos'} e ${mesesRestantes} ${mesesRestantes === 1 ? 'mês' : 'meses'} na empresa`
    : `${anosCompletos} ${anosCompletos === 1 ? 'ano' : 'anos'} na empresa`;
};

const mergeByKey = (current = [], incoming = [], key = 'id') => {
  const map = new Map();

  [...(Array.isArray(current) ? current : []), ...(Array.isArray(incoming) ? incoming : [])]
    .filter((item) => item && item[key] !== undefined && item[key] !== null && item[key] !== '')
    .forEach((item) => {
      const normalized = { ...item, [key]: String(item[key]) };
      map.set(normalized[key], { ...(map.get(normalized[key]) || {}), ...normalized });
    });

  return Array.from(map.values());
};

const mergeById = (current = [], incoming = []) => mergeByKey(current, incoming, 'id');
const mergeByName = (current = [], incoming = []) => mergeByKey(current, incoming, 'name');

const buildResolvedEntity = (ref, fallback = {}) => {
  if (!ref?.id) return null;

  return {
    ...fallback,
    ...(ref.data || {}),
    id: String(ref.id),
    label: ref.label
  };
};

const buildNamedOption = (name) => (
  name
    ? { id: String(name), name: String(name) }
    : null
);

const buildBenefitOption = (name) => (
  name && name !== 'Nenhuma'
    ? { id: String(name), name: String(name), value: 0 }
    : null
);

const TAB_LABELS = {
  personal: 'Dados Pessoais',
  contractual: 'Contratuais',
  banking: 'Bancários',
  dependents: 'Dependentes',
  beneficiarios: 'Beneficiários',
  documents: 'Documentos',
  benefits: 'Benefícios',
  calculations: 'Cálculos',
  geolocation: 'Geolocalização',
  escolaridade: 'Escolaridade',
  lms: 'Grid Learning',
  jornadas: 'Jornadas',
  feedback: 'Feedback',
  dcf: 'DCF',
  ponto: 'Acesso Ponto'
};

const DetailedEmployeeForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isEditing = Boolean(id);
  // Retorna à mesma página/busca da listagem de onde o usuário veio (ex: página 2)
  const returnPath = location.state?.from || '/funcionarios';

  const regimeInicial = searchParams.get('regime') || '';

  const defaultTab = 'personal';

  // Form states
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [formData, setFormData] = useState({
    // Photo
    foto: '',
    foto_file: null,

    // Personal data
    nome: '',
    cpf: '',
    rg_numero: '',
    rg_orgao: '',
    rg_uf: '',
    data_nascimento: '',
    sexo: '',
    nacionalidade: '',
    naturalidade: '',
    nome_mae: '',
    nome_pai: '',
    pis_pasep_nit: '',
    titulo_eleitor_numero: '',
    titulo_eleitor_zona: '',
    titulo_eleitor_secao: '',
    certificado_reservista: '',
    endereco: '',
    logradouro: '',
    cep: '',
    cidade: '',
    estado: '',
    bairro: '',
    numero: '',
    complemento: '',
    telefone: '',
    email: '',
    estado_civil: '',

    // CTPS data
    ctps_numero: '',
    ctps_serie: '',
    ctps_data_emissao: '',
    ctps_uf: '',

    // CNH data
    cnh_numero: '',
    cnh_categoria: '',
    cnh_validade: '',

    // Contractual data
    empresa: '',
    filiais: '',
    cargo: '',
    faixa: '',
    cbo: '',
    tipo_remuneracao: '',
    tipo_contrato: '',
    admissao: '',
    ferias_quitadas_ate: '',
    regime: regimeInicial,
    departamento: '',  // Mantido para compatibilidade
    departamentos: [], // Novo campo para múltiplos departamentos
    situacao: '',
    data_demissao: '',
    tipo_demissao_id: '',
    salario: 0,

    // Banking data
    banco: '',
    agencia: '',
    conta: '',
    tipo_conta: '',
    pix: '',

    // Benefits and calculations
    acumulo_funcao: 0,
    vt: 0,
    desconto_vt: 0,
    vt_custo: 0,
    taxa_vt: 0,
    repasse_vt: 0,
    transporte_id: '',
    va: 0,
    vr: 0,
    aux_combustivel: 0,
    hora_extra: 0,
    comissao: 0,
    ajuda_custo: 0,
    auxilio_creche: 0,
    segunda_refeicao: 0,
    pernoite: 0,
    assist_medica: 0,
    beneficios_outros: [],
    odonto: 'Nenhuma',
    seg_vida: 'Nenhuma',
    total_pass: 'Nenhuma',
    med_ocupacional: 'Nenhuma',
    nf_13: 0,
    possui_decimo_terceiro: true,
    prov_13: 0,
    prov_ferias: 0,
    fgts: 0,
    inss: 0,
    total: 0,

    // Geolocation data
    geolocation_origem: '',
    geolocation_destino: '',

    // Banco de Horas data
    acordo_banco_horas_id: '',
    jornada_id: '',
    modalidade_ponto: '',  // PIN, APP ou vazio (seguir padrão da empresa)
    optante_sindicato: true,

    // Geofences (Sistema de Ponto)
    geofence_ids: [],  // Array de IDs de geofences

    // Horários de Trabalho (Sistema de Ponto)
    horario_trabalho_ids: [],  // Array de IDs de horários

    // Novos campos - Escolaridade
    escolaridade_id: '',
    cursos: [],  // Array de cursos extracurriculares

    // Novos campos - Deficiência e Contato de Emergência
    tem_deficiencia: false,
    qual_deficiencia: '',
    contato_emergencia_nome: '',
    contato_emergencia_telefone: '',

    // Novos campos - CNPJ (para regime PJ)
    cnpj: '',

    // Novos campos - Período de Experiência
    dias_primeiro_periodo: '',
    experiencia_1periodo_inicio: '',
    experiencia_1periodo_fim: '',
    experiencia_2periodo_inicio: '',
    experiencia_2periodo_fim: '',

    // Novos campos - Contratuais adicionais
    matricula: '',
    superior_direto_id: '',
    grau_hierarquia_id: '',
    adiantamento: '',

    // Arrays
    dependentes: [],
    beneficiarios: [],
    documentos: [],
    adicionais: []
  });

  const [options, setOptions] = useState({
    companies: [],
    branches: [],
    departments: [],
    status: [],
    positionSalaryCargos: [],
    odontoOptions: [],
    segVidaOptions: [],
    totalPassOptions: [],
    medOcupacionalOptions: [],
    transporteOptions: [],
    escolaridadeOptions: [],
    funcionariosOptions: []  // Para Superior Direto
  });

  const [benefits, setBenefits] = useState({
    plano_saude_enabled: false,
    odonto_enabled: false,
    seg_vida_enabled: false,
    total_pass_enabled: false,
    med_ocupacional_enabled: false
  });

  // Geolocation states
  const [geolocationData, setGeolocationData] = useState(null);
  const [geolocationLoading, setGeolocationLoading] = useState(false);
  const [defaultAddress, setDefaultAddress] = useState(null);

  // Salary advances data for adiantamento calculation
  const [salaryAdvances, setSalaryAdvances] = useState([]);

  // Add data loading state to prevent premature rendering
  const [dataLoaded, setDataLoaded] = useState(false);

  // Employee evaluation data states
  const [employeeEvaluationData, setEmployeeEvaluationData] = useState({
    discAssessment: null,
    pdiData: null,
    lmsCourses: 0,
    lmsCoursesData: {
      completed: 0,
      inProgress: 0,
      notStarted: 0,
      total: 0
    },
    performanceEvaluation: null,
    nineBoxPosition: null
  });

  // LMS data states
  const [lmsData, setLmsData] = useState({
    allCourses: [],
    assignedCourses: [],
    loading: false
  });

  // Banco de Horas data states
  const [bancoHorasData, setBancoHorasData] = useState({
    acordos: [],
    jornadas: [],
    loading: false
  });

  // Tipos de Demissão data state
  const [tiposDemissao, setTiposDemissao] = useState([]);

  // Etnias data state
  const [etnias, setEtnias] = useState([]);

  // Graus de Hierarquia data state
  const [grausHierarquia, setGrausHierarquia] = useState([]);

  // PIN data states (Ponto-App)
  const [pinData, setPinData] = useState({
    pin: null,
    tem_pin: false,
    loading: false,
    generating: false,
    showDialog: null
  });

  // Geofences data states
  const [geofencesData, setGeofencesData] = useState({
    geofences: [],
    loading: false
  });

  const [resourceLoading, setResourceLoading] = useState({});
  const [loadedResources, setLoadedResources] = useState({});
  const loadedResourcesRef = useRef({});
  const inflightResourcesRef = useRef({});

  const markResourceLoaded = useCallback((key) => {
    loadedResourcesRef.current[key] = true;
    setLoadedResources(prev => ({ ...prev, [key]: true }));
  }, []);

  const loadResource = useCallback(async (key, loader) => {
    if (loadedResourcesRef.current[key]) {
      return;
    }

    if (inflightResourcesRef.current[key]) {
      return inflightResourcesRef.current[key];
    }

    setResourceLoading(prev => ({ ...prev, [key]: true }));

    const request = Promise.resolve()
      .then(loader)
      .then(() => {
        markResourceLoaded(key);
      })
      .catch((error) => {
        console.error(`Erro ao carregar recurso ${key}:`, error);
        throw error;
      })
      .finally(() => {
        delete inflightResourcesRef.current[key];
        setResourceLoading(prev => ({ ...prev, [key]: false }));
      });

    inflightResourcesRef.current[key] = request;
    return request;
  }, [markResourceLoaded]);

  const ensureEtniasLoaded = useCallback(() => loadResource('etnias', async () => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API}/api/etnias`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setEtnias(prev => mergeById(prev, Array.isArray(response.data) ? response.data : []));
  }), [loadResource]);

  const ensureContractualMetaLoaded = useCallback(() => loadResource('contractualMeta', async () => {
    const token = localStorage.getItem('token');
    const [statusRes, tiposDemissaoRes, grausHierarquiaRes] = await Promise.all([
      axios.get(`${API}/api/status`),
      axios.get(`${API}/api/tipos-demissao`, {
        headers: { Authorization: `Bearer ${token}` }
      }),
      axios.get(`${API}/api/graus-hierarquia`, {
        headers: { Authorization: `Bearer ${token}` }
      })
    ]);

    setOptions(prev => ({
      ...prev,
      status: mergeByName(prev.status, Array.isArray(statusRes.data) ? statusRes.data : [])
    }));
    setTiposDemissao(prev => mergeById(prev, Array.isArray(tiposDemissaoRes.data) ? tiposDemissaoRes.data : []));
    setGrausHierarquia(prev => mergeById(prev, Array.isArray(grausHierarquiaRes.data) ? grausHierarquiaRes.data : []));
  }), [loadResource]);

  const ensureCompaniesLoaded = useCallback(() => loadResource('companies', async () => {
    const response = await axios.get(`${API}/api/companies`);
    setOptions(prev => ({
      ...prev,
      companies: mergeById(prev.companies, Array.isArray(response.data) ? response.data : [])
    }));
  }), [loadResource]);

  const ensureBranchesLoaded = useCallback(() => loadResource('branches', async () => {
    const response = await axios.get(`${API}/api/branches`);
    setOptions(prev => ({
      ...prev,
      branches: mergeByName(prev.branches, Array.isArray(response.data) ? response.data : [])
    }));
  }), [loadResource]);

  const ensurePositionSalaryCargosLoaded = useCallback(() => loadResource('positionSalaryCargos', async () => {
    const response = await axios.get(`${API}/api/position-salaries/cargos`);
    setOptions(prev => ({
      ...prev,
      positionSalaryCargos: mergeByName(prev.positionSalaryCargos, Array.isArray(response.data) ? response.data : [])
    }));
  }), [loadResource]);

  const ensureDepartmentsLoaded = useCallback(() => loadResource('departments', async () => {
    const response = await axios.get(`${API}/api/departments`);
    setOptions(prev => ({
      ...prev,
      departments: Array.isArray(response.data) ? response.data : []
    }));
  }), [loadResource]);

  const ensureSalaryAdvancesLoaded = useCallback(() => loadResource('salaryAdvances', async () => {
    const response = await axios.get(`${API}/api/salary-advances`);
    setSalaryAdvances(Array.isArray(response.data) ? response.data : []);
  }), [loadResource]);

  const ensureFuncionariosOptionsLoaded = useCallback(() => loadResource('funcionariosOptions', async () => {
    const response = await axios.get(`${API}/api/employees/lite`);
    const employees = Array.isArray(response.data) ? response.data : [];
    setOptions(prev => ({
      ...prev,
      funcionariosOptions: mergeById(
        prev.funcionariosOptions,
        employees.filter(employee => employee.id !== id)
      )
    }));
  }), [id, loadResource]);

  const ensureBenefitsOptionsLoaded = useCallback(() => loadResource('benefitsOptions', async () => {
    const [odontoRes, segVidaRes, totalPassRes, medOcupacionalRes, transporteRes] = await Promise.all([
      axios.get(`${API}/api/odonto-options`),
      axios.get(`${API}/api/seg-vida-options`),
      axios.get(`${API}/api/total-pass-options`),
      axios.get(`${API}/api/med-ocupacional-options`),
      axios.get(`${API}/api/transporte`)
    ]);

    setOptions(prev => ({
      ...prev,
      odontoOptions: mergeByName(prev.odontoOptions, Array.isArray(odontoRes.data) ? odontoRes.data : []),
      segVidaOptions: mergeByName(prev.segVidaOptions, Array.isArray(segVidaRes.data) ? segVidaRes.data : []),
      totalPassOptions: mergeByName(prev.totalPassOptions, Array.isArray(totalPassRes.data) ? totalPassRes.data : []),
      medOcupacionalOptions: mergeByName(prev.medOcupacionalOptions, Array.isArray(medOcupacionalRes.data) ? medOcupacionalRes.data : []),
      transporteOptions: mergeById(prev.transporteOptions, Array.isArray(transporteRes.data) ? transporteRes.data : [])
    }));
  }), [loadResource]);

  const ensureEscolaridadeOptionsLoaded = useCallback(() => loadResource('escolaridadeOptions', async () => {
    const response = await axios.get(`${API}/api/cadastros/escolaridade`);
    setOptions(prev => ({
      ...prev,
      escolaridadeOptions: mergeById(prev.escolaridadeOptions, Array.isArray(response.data) ? response.data : [])
    }));
  }), [loadResource]);

  const ensureDefaultAddressLoaded = useCallback(() => loadResource('defaultAddress', async () => {
    try {
      const response = await axios.get(`${API}/api/company-addresses/default`);
      setDefaultAddress(response.data || null);
    } catch (error) {
      setDefaultAddress(null);
      if (error?.response?.status && error.response.status !== 404) {
        throw error;
      }
    }
  }), [loadResource]);

  const fetchEmployeeEvaluationData = async () => {
    if (!id) return;

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // 1. Fetch DISC Assessment - Buscar por employee_id ou email
      let discAssessment = null;
      try {
        const discResponse = await axios.get(`${API}/api/disc/assessments/employee/${id}`, { headers });
        const assessments = discResponse.data || [];

        // Pegar a avaliação mais recente se houver múltiplas
        if (assessments.length > 0) {
          discAssessment = assessments.reduce((latest, current) => {
            const latestDate = new Date(latest.assessment_date || latest.created_at || 0);
            const currentDate = new Date(current.assessment_date || current.created_at || 0);
            return currentDate > latestDate ? current : latest;
          });
        }
      } catch (error) {
        console.log('DISC não encontrado para este colaborador');
      }

      // 2. Fetch PDI Data - Buscar de pdis_avancados
      let pdiData = null;
      try {
        const pdiResponse = await axios.get(`${API}/api/pdi-avancado/colaborador/${id}`, { headers });
        const pdis = pdiResponse.data || [];
        // Pegar o PDI mais recente ativo
        pdiData = pdis.find(p => p.status === 'ativo') || pdis[0] || null;
      } catch (error) {
        console.log('PDI não encontrado para este colaborador');
      }

      // 3. Fetch Performance Evaluation - Buscar de avaliacoes_desempenho
      let performanceEvaluation = null;
      try {
        const perfResponse = await axios.get(`${API}/api/avaliacoes/desempenho?colaborador_id=${id}`, { headers });
        const avaliacoes = perfResponse.data || [];

        if (avaliacoes.length > 0) {
          // Pegar a avaliação mais recente
          performanceEvaluation = avaliacoes.reduce((latest, current) => {
            const latestDate = new Date(latest.data_avaliacao || latest.criado_em || latest.created_at);
            const currentDate = new Date(current.data_avaliacao || current.criado_em || current.created_at);
            return currentDate > latestDate ? current : latest;
          });
        }
      } catch (error) {
        console.log('Avaliação de desempenho não encontrada para este colaborador');
      }

      // 4. Fetch 9Box Position - Buscar posição mais recente
      let nineBoxPosition = null;
      try {
        const nineBoxResponse = await axios.get(`${API}/api/9box/posicoes?colaborador_id=${id}`, { headers });
        const posicoes = nineBoxResponse.data || [];

        if (posicoes.length > 0) {
          // Pegar a posição mais recente
          nineBoxPosition = posicoes.reduce((latest, current) => {
            const latestDate = new Date(latest.criado_em || latest.created_at);
            const currentDate = new Date(current.criado_em || current.created_at);
            return currentDate > latestDate ? current : latest;
          });
        }
      } catch (error) {
        console.log('Posição 9Box não encontrada para este colaborador');
      }

      setEmployeeEvaluationData(prev => ({
        ...prev,
        discAssessment,
        pdiData,
        performanceEvaluation,
        nineBoxPosition
      }));

    } catch (error) {
      console.error('Erro ao carregar dados de avaliação:', error);
      // Não mostrar toast de erro pois são dados opcionais
    }
  };

  const fetchLMSData = async () => {
    if (!id) return;

    try {
      setLmsData(prev => ({ ...prev, loading: true }));

      // Buscar todos os cursos disponíveis
      const coursesResponse = await axios.get(`${API}/api/lms/courses`);
      const allCourses = Array.isArray(coursesResponse.data) ? coursesResponse.data : [];

      // Buscar cursos atribuídos ao funcionário
      let assignedCourses = [];
      try {
        const assignedResponse = await axios.get(`${API}/api/employees/${id}/lms-courses`);
        assignedCourses = Array.isArray(assignedResponse.data) ? assignedResponse.data : [];
      } catch (assignedError) {
        console.log('No assigned courses found for employee:', assignedError);
        assignedCourses = [];
      }

      setLmsData({
        allCourses,
        assignedCourses,
        loading: false
      });

      setEmployeeEvaluationData(prev => ({
        ...prev,
        lmsCourses: assignedCourses.filter(course => course.status === 'completed').length,
        lmsCoursesData: {
          completed: assignedCourses.filter(course => course.status === 'completed').length,
          inProgress: assignedCourses.filter(course => course.progress_percentage > 0 && course.status !== 'completed').length,
          notStarted: assignedCourses.filter(course => course.progress_percentage === 0).length,
          total: assignedCourses.length
        }
      }));
    } catch (error) {
      console.error('Erro ao carregar dados Grid Learning:', error);
      setLmsData({
        allCourses: [],
        assignedCourses: [],
        loading: false
      });
      setEmployeeEvaluationData(prev => ({
        ...prev,
        lmsCourses: 0,
        lmsCoursesData: {
          completed: 0,
          inProgress: 0,
          notStarted: 0,
          total: 0
        }
      }));
      // Não mostrar toast de erro pois são dados opcionais
    }
  };

  const fetchBancoHorasData = async () => {
    try {
      setBancoHorasData(prev => ({ ...prev, loading: true }));

      // Buscar acordos de banco de horas disponíveis
      const acordosResponse = await axios.get(`${API}/api/banco-horas/acordos`);
      const acordos = Array.isArray(acordosResponse.data) ? acordosResponse.data : [];

      // Buscar jornadas de trabalho disponíveis
      const jornadasResponse = await axios.get(`${API}/api/banco-horas/jornadas`);
      const jornadas = Array.isArray(jornadasResponse.data) ? jornadasResponse.data : [];

      setBancoHorasData(prev => ({
        ...prev,
        acordos: mergeById(prev.acordos, acordos),
        jornadas: mergeById(prev.jornadas, jornadas),
        loading: false
      }));
    } catch (error) {
      console.error('Erro ao carregar dados de Banco de Horas:', error);
      setBancoHorasData({
        acordos: [],
        jornadas: [],
        loading: false
      });
      // Não mostrar toast de erro pois são dados opcionais
    }
  };

  const fetchGeofencesData = async () => {
    try {
      setGeofencesData(prev => ({ ...prev, loading: true }));
      const token = localStorage.getItem('token');

      // Buscar geofences ativas disponíveis
      const geofencesResponse = await axios.get(`${API}/api/timetracking/geofences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const geofences = Array.isArray(geofencesResponse.data) ? geofencesResponse.data.filter(g => g.ativo) : [];

      setGeofencesData(prev => ({
        ...prev,
        geofences: mergeById(prev.geofences, geofences),
        loading: false
      }));
    } catch (error) {
      console.error('Erro ao carregar geofences:', error);
      setGeofencesData({
        geofences: [],
        loading: false
      });
    }
  };

  const fetchPinData = async () => {
    if (!id) return;
    try {
      setPinData(prev => ({ ...prev, loading: true }));
      const r = await axios.get(`${API}/api/ponto-app/pins`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      const employees = r.data || [];
      const match = employees.find(e => e.employee_id === id);
      setPinData(prev => ({
        ...prev,
        tem_pin: match?.tem_pin || false,
        pin: match?.pin || null,
        loading: false
      }));
    } catch (e) {
      console.error('Erro ao carregar PIN:', e);
      setPinData(prev => ({ ...prev, loading: false }));
    }
  };

  const gerarPin = async () => {
    setPinData(prev => ({ ...prev, generating: true }));
    try {
      const r = await axios.post(`${API}/api/ponto-app/pins/gerar?employee_id=${id}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setPinData(prev => ({ ...prev, showDialog: r.data, tem_pin: true }));
      fetchPinData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar PIN");
    } finally {
      setPinData(prev => ({ ...prev, generating: false }));
    }
  };

  const resetPin = async () => {
    if (!window.confirm("Tem certeza? O PIN atual será substituído.")) return;
    gerarPin();
  };

  // Helper functions for evaluation data formatting
  const getDISCBadgeInfo = (assessment) => {
    if (!assessment) return { letter: 'N', color: 'bg-gray-400', label: 'Não Avaliado' };

    const colors = {
      'D': { color: 'bg-red-500', label: 'Dominante' },
      'I': { color: 'bg-yellow-500', label: 'Influente' },
      'S': { color: 'bg-green-500', label: 'Estável' },
      'C': { color: 'bg-blue-500', label: 'Cauteloso' }
    };

    const profile = assessment.primary_style;
    return {
      letter: profile,
      color: colors[profile]?.color || 'bg-gray-400',
      label: colors[profile]?.label || 'Não Identificado',
      scores: {
        D: assessment.d_score,
        I: assessment.i_score,
        S: assessment.s_score,
        C: assessment.c_score
      }
    };
  };

  const getPDIInfo = (pdi) => {
    if (!pdi) return { hasPDI: false, status: 'Não possui', color: 'text-gray-500', title: '', goals: 0 };

    const statusColors = {
      'ativo': 'text-green-600',
      'concluido': 'text-blue-600',
      'pendente': 'text-yellow-600',
      'rascunho': 'text-gray-600'
    };

    const statusLabels = {
      'ativo': 'Em Andamento',
      'concluido': 'Concluído',
      'pendente': 'Pendente',
      'rascunho': 'Rascunho'
    };

    // Contar metas (pode estar em development_goals ou acoes)
    const goalsCount = pdi.development_goals?.length || pdi.acoes?.length || pdi.objetivos?.length || 0;

    return {
      hasPDI: true,
      status: statusLabels[pdi.status] || pdi.status || 'Em Andamento',
      color: statusColors[pdi.status] || 'text-green-600',
      title: pdi.title || pdi.nome || pdi.titulo || 'PDI Personalizado',
      goals: goalsCount
    };
  };

  const getPerformanceInfo = (evaluation) => {
    if (!evaluation) return { hasEvaluation: false, summary: 'Não avaliado', color: 'text-gray-500', score: 'N/A', date: 'N/A' };

    // Tentar pegar a nota de diferentes estruturas possíveis
    let percentage = 0;

    if (evaluation.resultado_final?.nota_final) {
      // Estrutura com resultado_final
      percentage = evaluation.resultado_final.nota_final;
    } else if (evaluation.nota_final) {
      percentage = evaluation.nota_final;
    } else if (evaluation.answers && evaluation.answers.length > 0) {
      // Calcular média das respostas
      const averageScore = evaluation.answers.reduce((sum, answer) => sum + (answer.score || 0), 0) / evaluation.answers.length;
      percentage = (averageScore / 5) * 100;
    } else if (evaluation.score) {
      percentage = evaluation.score;
    }

    const getPerformanceLevel = (score) => {
      if (score >= 80) return { level: 'Excelente', color: 'text-green-600' };
      if (score >= 70) return { level: 'Bom', color: 'text-blue-600' };
      if (score >= 60) return { level: 'Regular', color: 'text-yellow-600' };
      return { level: 'Precisa Melhorar', color: 'text-red-600' };
    };

    const performanceLevel = getPerformanceLevel(percentage);

    // Tentar pegar data de diferentes campos
    const dateField = evaluation.data_avaliacao || evaluation.criado_em || evaluation.created_at;
    const formattedDate = dateField ? new Date(dateField).toLocaleDateString('pt-BR') : 'N/A';

    return {
      hasEvaluation: true,
      summary: performanceLevel.level,
      color: performanceLevel.color,
      score: percentage > 0 ? percentage.toFixed(1) : 'N/A',
      date: formattedDate
    };
  };

  const getNineBoxInfo = (position) => {
    if (!position) return { hasPosition: false, status: 'Não posicionado', color: 'text-gray-500 bg-gray-50', icon: '📊', performance: 'N/A', potential: 'N/A' };

    const positions = {
      'high_performance_high_potential': { label: 'Estrela', color: 'text-yellow-600 bg-yellow-50', icon: '⭐' },
      'high_performance_medium_potential': { label: 'Talento', color: 'text-green-600 bg-green-50', icon: '🌟' },
      'high_performance_low_potential': { label: 'Especialista', color: 'text-blue-600 bg-blue-50', icon: '🎯' },
      'medium_performance_high_potential': { label: 'Potencial', color: 'text-purple-600 bg-purple-50', icon: '🚀' },
      'medium_performance_medium_potential': { label: 'Core', color: 'text-indigo-600 bg-indigo-50', icon: '💎' },
      'medium_performance_low_potential': { label: 'Consistente', color: 'text-teal-600 bg-teal-50', icon: '🔒' },
      'low_performance_high_potential': { label: 'Enigma', color: 'text-orange-600 bg-orange-50', icon: '❓' },
      'low_performance_medium_potential': { label: 'Dilemma', color: 'text-red-600 bg-red-50', icon: '⚠️' },
      'low_performance_low_potential': { label: 'Underperformer', color: 'text-gray-600 bg-gray-50', icon: '📉' },

      // Aliases antigos para compatibilidade
      'top-right': { label: 'Estrela', color: 'text-yellow-600 bg-yellow-50', icon: '⭐' },
      'top-center': { label: 'Talento', color: 'text-green-600 bg-green-50', icon: '🌟' },
      'top-left': { label: 'Especialista', color: 'text-blue-600 bg-blue-50', icon: '🎯' },
      'center-right': { label: 'Potencial', color: 'text-purple-600 bg-purple-50', icon: '🚀' },
      'center-center': { label: 'Core', color: 'text-indigo-600 bg-indigo-50', icon: '💎' },
      'center-left': { label: 'Consistente', color: 'text-teal-600 bg-teal-50', icon: '🔒' },
      'bottom-right': { label: 'Enigma', color: 'text-orange-600 bg-orange-50', icon: '❓' },
      'bottom-center': { label: 'Dilemma', color: 'text-red-600 bg-red-50', icon: '⚠️' },
      'bottom-left': { label: 'Underperformer', color: 'text-gray-600 bg-gray-50', icon: '📉' }
    };

    const positionKey = position.posicao || position.position;
    const positionInfo = positions[positionKey] || positions['center-center'];

    // Extrair performance e potential
    let performance = 'N/A';
    let potential = 'N/A';

    if (position.score_desempenho !== undefined && position.score_desempenho !== null) {
      performance = position.score_desempenho.toFixed(1);
    } else if (position.performance_level) {
      performance = position.performance_level;
    }

    if (position.score_potencial !== undefined && position.score_potencial !== null) {
      potential = position.score_potencial.toFixed(1);
    } else if (position.potential_level) {
      potential = position.potential_level;
    }

    return {
      hasPosition: true,
      status: positionInfo.label,
      color: positionInfo.color,
      icon: positionInfo.icon,
      performance,
      potential
    };
  };

  const loadTabResources = useCallback(async (tab) => {
    if (!user) return;

    if (tab === 'personal') {
      await ensureEtniasLoaded();
      return;
    }

    if (tab === 'contractual') {
      await Promise.all([
        ensureContractualMetaLoaded(),
        ensureBranchesLoaded(),
        ensurePositionSalaryCargosLoaded(),
        ensureSalaryAdvancesLoaded()
      ]);
      return;
    }

    if (tab === 'benefits' || tab === 'calculations') {
      await ensureBenefitsOptionsLoaded();
      return;
    }

    if (tab === 'geolocation') {
      await ensureDefaultAddressLoaded();
      return;
    }

    if (tab === 'escolaridade') {
      await ensureEscolaridadeOptionsLoaded();
      return;
    }

    if (tab === 'lms' && isEditing && id) {
      await loadResource('lmsData', fetchLMSData);
      return;
    }

    if (tab === 'jornadas') {
      await Promise.all([
        loadResource('bancoHorasData', fetchBancoHorasData),
        loadResource('geofencesData', fetchGeofencesData)
      ]);
      return;
    }

    if (tab === 'feedback' && isEditing && id) {
      await loadResource('employeeEvaluationData', fetchEmployeeEvaluationData);
      return;
    }

    if (tab === 'ponto' && isEditing && id) {
      await loadResource('pinData', fetchPinData);
    }
  }, [
    user,
    isEditing,
    id,
    ensureEtniasLoaded,
    ensureContractualMetaLoaded,
    ensureBranchesLoaded,
    ensurePositionSalaryCargosLoaded,
    ensureSalaryAdvancesLoaded,
    ensureBenefitsOptionsLoaded,
    ensureDefaultAddressLoaded,
    ensureEscolaridadeOptionsLoaded,
    loadResource
  ]);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const bootstrap = async () => {
      try {
        if (isEditing && id) {
          await fetchEmployee();
        } else if (!cancelled) {
          setDataLoaded(true);
        }
      } finally {
        if (!cancelled && !isEditing) {
          setDataLoaded(true);
        }
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [isEditing, id, user]);

  useEffect(() => {
    loadTabResources(activeTab);
  }, [activeTab, loadTabResources]);

  // Cálculo automático dos períodos de experiência
  useEffect(() => {
    const diasPrimeiroPeriodo = parseInt(formData.dias_primeiro_periodo);
    const dataAdmissao = formData.admissao;

    // Validações
    if (!diasPrimeiroPeriodo || !dataAdmissao) return;
    if (diasPrimeiroPeriodo < 10 || diasPrimeiroPeriodo > 80) return;

    try {
      // Converter data de admissão para objeto Date
      const admissaoDate = new Date(dataAdmissao + 'T00:00:00');

      // 1º Período - Início = Data de Admissão
      const primeiroPeriodoInicio = new Date(admissaoDate);

      // 1º Período - Fim = Data de Admissão + (diasPrimeiroPeriodo - 1)
      // porque o dia da admissão entra na contagem do prazo.
      const primeiroPeriodoFim = new Date(admissaoDate);
      primeiroPeriodoFim.setDate(primeiroPeriodoFim.getDate() + diasPrimeiroPeriodo - 1);

      // 2º Período - Início = 1º Período Fim + 1 dia
      const segundoPeriodoInicio = new Date(primeiroPeriodoFim);
      segundoPeriodoInicio.setDate(segundoPeriodoInicio.getDate() + 1);

      // 2º Período - Fim = 2º Período Início + (90 - diasPrimeiroPeriodo) dias
      const diasSegundoPeriodo = 90 - diasPrimeiroPeriodo;
      const segundoPeriodoFim = new Date(segundoPeriodoInicio);
      segundoPeriodoFim.setDate(segundoPeriodoFim.getDate() + diasSegundoPeriodo - 1); // -1 porque já começou no dia seguinte

      // Formatar datas para input type="date" (YYYY-MM-DD)
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Atualizar formData com as datas calculadas
      setFormData(prev => ({
        ...prev,
        experiencia_1periodo_inicio: formatDate(primeiroPeriodoInicio),
        experiencia_1periodo_fim: formatDate(primeiroPeriodoFim),
        experiencia_2periodo_inicio: formatDate(segundoPeriodoInicio),
        experiencia_2periodo_fim: formatDate(segundoPeriodoFim)
      }));

    } catch (error) {
      console.error('Erro ao calcular períodos de experiência:', error);
    }
  }, [formData.dias_primeiro_periodo, formData.admissao]);

  const fetchEmployee = async () => {
    try {
      const [employeeResponse, dpResponse] = await Promise.all([
        axios.get(`${API}/api/employees-detailed/${id}`),
        axios.get(`${API}/api/calculos-dp/employees/${id}/dp-data`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }).catch(() => ({ data: { dp_data: {} } }))
      ]);
      const employee = employeeResponse.data;
      const dpData = dpResponse?.data?.dp_data || {};
      const selectedCompanyId = typeof employee.company_id === 'object'
        ? employee.company_id?.id || ''
        : (employee.company_id || '');
      const resolvedCompany = employee.company_ref?.id
        ? buildResolvedEntity(employee.company_ref, {
          name: employee.company_ref?.data?.name || employee.empresa || employee.company_ref?.label
        })
        : (selectedCompanyId && employee.empresa
          ? { id: String(selectedCompanyId), name: employee.empresa }
          : null);
      const resolvedSuperior = buildResolvedEntity(employee.superior_direto_ref, {
        nome: employee.superior_direto_ref?.data?.nome || employee.superior_direto_ref?.label,
        cargo: employee.superior_direto_ref?.data?.cargo || ''
      });
      const resolvedTransporte = buildResolvedEntity(employee.transporte_ref, {
        name: employee.transporte_ref?.data?.name || employee.transporte_ref?.label,
        value: employee.transporte_ref?.data?.value ?? 0
      });
      const resolvedEscolaridade = buildResolvedEntity(employee.escolaridade_ref, {
        nome: employee.escolaridade_ref?.data?.nome || employee.escolaridade_ref?.label
      });
      const resolvedTipoDemissao = buildResolvedEntity(employee.tipo_demissao_ref, {
        nome: employee.tipo_demissao_ref?.data?.nome || employee.tipo_demissao_ref?.label
      });
      const resolvedGrauHierarquia = buildResolvedEntity(employee.grau_hierarquia_ref, {
        nome: employee.grau_hierarquia_ref?.data?.nome || employee.grau_hierarquia_ref?.label,
        nivel: employee.grau_hierarquia_ref?.data?.nivel
      });
      const resolvedEtnia = buildResolvedEntity(employee.etnia_ref, {
        nome: employee.etnia_ref?.data?.nome || employee.etnia_ref?.label
      });
      const resolvedAcordoBancoHoras = buildResolvedEntity(employee.acordo_banco_horas_ref, {
        nome: employee.acordo_banco_horas_ref?.data?.nome || employee.acordo_banco_horas_ref?.label
      });
      const resolvedJornada = buildResolvedEntity(employee.jornada_ref, {
        nome: employee.jornada_ref?.data?.nome || employee.jornada_ref?.label
      });
      const resolvedGeofences = Array.isArray(employee.geofence_refs)
        ? employee.geofence_refs
          .map((ref) => buildResolvedEntity(ref, {
            nome: ref?.data?.nome || ref?.label
          }))
          .filter(Boolean)
        : [];

      setOptions(prev => ({
        ...prev,
        companies: resolvedCompany ? mergeById(prev.companies, [resolvedCompany]) : prev.companies,
        branches: employee.filiais ? mergeByName(prev.branches, [buildNamedOption(employee.filiais)]) : prev.branches,
        status: employee.situacao ? mergeByName(prev.status, [buildNamedOption(employee.situacao)]) : prev.status,
        positionSalaryCargos: employee.cargo ? mergeByName(prev.positionSalaryCargos, [buildNamedOption(employee.cargo)]) : prev.positionSalaryCargos,
        transporteOptions: resolvedTransporte ? mergeById(prev.transporteOptions, [resolvedTransporte]) : prev.transporteOptions,
        escolaridadeOptions: resolvedEscolaridade ? mergeById(prev.escolaridadeOptions, [resolvedEscolaridade]) : prev.escolaridadeOptions,
        funcionariosOptions: resolvedSuperior ? mergeById(prev.funcionariosOptions, [resolvedSuperior]) : prev.funcionariosOptions,
        odontoOptions: employee.odonto ? mergeByName(prev.odontoOptions, [buildBenefitOption(employee.odonto)].filter(Boolean)) : prev.odontoOptions,
        segVidaOptions: employee.seg_vida ? mergeByName(prev.segVidaOptions, [buildBenefitOption(employee.seg_vida)].filter(Boolean)) : prev.segVidaOptions,
        totalPassOptions: employee.total_pass ? mergeByName(prev.totalPassOptions, [buildBenefitOption(employee.total_pass)].filter(Boolean)) : prev.totalPassOptions,
        medOcupacionalOptions: employee.med_ocupacional ? mergeByName(prev.medOcupacionalOptions, [buildBenefitOption(employee.med_ocupacional)].filter(Boolean)) : prev.medOcupacionalOptions
      }));

      if (resolvedTipoDemissao) {
        setTiposDemissao(prev => mergeById(prev, [resolvedTipoDemissao]));
      }
      if (resolvedGrauHierarquia) {
        setGrausHierarquia(prev => mergeById(prev, [resolvedGrauHierarquia]));
      }
      if (resolvedEtnia) {
        setEtnias(prev => mergeById(prev, [resolvedEtnia]));
      }
      if (resolvedAcordoBancoHoras || resolvedJornada) {
        setBancoHorasData(prev => ({
          ...prev,
          acordos: resolvedAcordoBancoHoras ? mergeById(prev.acordos, [resolvedAcordoBancoHoras]) : prev.acordos,
          jornadas: resolvedJornada ? mergeById(prev.jornadas, [resolvedJornada]) : prev.jornadas
        }));
      }
      if (resolvedGeofences.length > 0) {
        setGeofencesData(prev => ({
          ...prev,
          geofences: mergeById(prev.geofences, resolvedGeofences)
        }));
      }

      // Apply array safety checks BEFORE setting formData
      const safeEmployee = {
        ...employee,
        empresa: employee.empresa || employee.company_ref?.label || '',
        admissao: employee.admissao ? employee.admissao.split('T')[0] : '',
        ferias_quitadas_ate: employee.ferias_quitadas_ate ? employee.ferias_quitadas_ate.split('T')[0] : '',
        data_nascimento: employee.data_nascimento ? employee.data_nascimento.split('T')[0] : '',
        ctps_data_emissao: employee.ctps_data_emissao ? employee.ctps_data_emissao.split('T')[0] : '',
        cnh_validade: employee.cnh_validade ? employee.cnh_validade.split('T')[0] : '',
        // Ensure company_id is always a string (not an object)
        company_id: typeof employee.company_id === 'object' ? employee.company_id?.id || '' : (employee.company_id || ''),
        // Ensure arrays are always arrays, never null
        dependentes: Array.isArray(employee.dependentes)
          ? employee.dependentes.map(dep => ({
            ...dep,
            beneficios: Array.isArray(dep.beneficios)
              ? dep.beneficios.map(beneficio => ({
                ...beneficio,
                valor: formatDependentBenefitCurrency(beneficio.valor),
              }))
              : [],
          }))
          : [],
        documentos: Array.isArray(employee.documentos) ? employee.documentos : [],
        adicionais: Array.isArray(employee.adicionais) ? employee.adicionais : [],
        // Converter departamento para departamentos (suporte a múltiplos)
        departamentos: Array.isArray(employee.departamentos)
          ? employee.departamentos
          : (employee.departamento ? [employee.departamento] : []),
        // Migrar beneficiarios: converter dependentes_vinculados de inteiros p/ objetos
        beneficiarios: Array.isArray(employee.beneficiarios)
          ? employee.beneficiarios.map(b => ({
            ...b,
            dependentes_vinculados: (b.dependentes_vinculados || []).map(v =>
              typeof v === 'object' ? v : {
                dependente_index: v,
                tipo_calculo: b.tipo_calculo || 'valor_fixo',
                valor_pagar: b.valor_pagar || 0
              }
            )
          }))
          : [],
        // Migrar beneficios_outros: suportar formato antigo (string única) e novo (array)
        beneficios_outros: Array.isArray(employee.beneficios_outros)
          ? employee.beneficios_outros
          : (employee.beneficios_outros
            ? [{ descricao: String(employee.beneficios_outros), valor: Number(employee.beneficios_outros_valor) || 0 }]
            : []),
        optante_sindicato: dpData.optante_sindicato !== false
      };

      setFormData(prev => calculateEmployeeFields({ ...prev, ...safeEmployee }));

      // Set benefit checkboxes
      setBenefits({
        plano_saude_enabled: employee.assist_medica > 0,
        odonto_enabled: employee.odonto !== 'Nenhuma' && employee.odonto !== '',
        seg_vida_enabled: employee.seg_vida !== 'Nenhuma' && employee.seg_vida !== '',
        total_pass_enabled: employee.total_pass !== 'Nenhuma' && employee.total_pass !== '',
        med_ocupacional_enabled: employee.med_ocupacional !== 'Nenhuma' && employee.med_ocupacional !== ''
      });

      // Mark data as loaded
      setDataLoaded(true);

    } catch (error) {
      console.error('Erro ao carregar funcionário:', error);
      toast.error('Erro ao carregar dados do funcionário');
      navigate(returnPath);
    }
  };

  // Helpers para Outros Benefícios dinâmicos
  const addOutroBeneficio = () => {
    setFormData(prev => calculateEmployeeFields({
      ...prev,
      beneficios_outros: [...(prev.beneficios_outros || []), { descricao: '', valor: 0 }]
    }));
  };

  const removeOutroBeneficio = (index) => {
    setFormData(prev => calculateEmployeeFields({
      ...prev,
      beneficios_outros: (prev.beneficios_outros || []).filter((_, i) => i !== index)
    }));
  };

  const updateOutroBeneficio = (index, field, value) => {
    setFormData(prev => {
      const novos = [...(prev.beneficios_outros || [])];
      if (novos[index]) {
        novos[index] = { ...novos[index], [field]: value };
      }
      return calculateEmployeeFields({ ...prev, beneficios_outros: novos });
    });
  };

  // Function to calculate employee fields based on regime (mirrors EmployeeForm logic)
  const calculateEmployeeFields = (data) => {
    const regime = data.regime || '';
    const salario = parseFloat(data.salario) || 0;
    const vt = parseFloat(data.vt) || 0;

    // Create a copy to avoid mutation
    const newData = { ...data };

    if (regime === 'PJ') {
      // For PJ, set specific fields to 0 and calculate 13 NF only if not manually set
      newData.vt = 0;
      newData.desconto_vt = 0;
      newData.vt_custo = 0;
      newData.va = 0;
      newData.vr = 0;
      newData.auxilio_creche = 0;
      newData.segunda_refeicao = 0;
      newData.pernoite = 0;
      newData.med_ocupacional = 'Nenhuma';
      newData.prov_13 = 0;
      newData.prov_ferias = 0;
      newData.fgts = 0;
      newData.inss = 0;
      // Only auto-calculate 13 NF if salary changed and it's not manually edited
      if (!newData.nf_13_manual_edit && salario > 0) {
        newData.nf_13 = newData.possui_decimo_terceiro !== false ? salario / 12 : 0;
      }
    } else if (regime === 'CLT') {
      // For CLT, calculate specific fields and set 13 NF to 0 only if not manually set
      if (!newData.nf_13_manual_edit) {
        newData.nf_13 = 0;
      }
      // Only calculate desconto_vt if VT > 0
      if (vt > 0) {
        newData.desconto_vt = Math.min(vt * 22, salario * 0.06);
      } else {
        newData.desconto_vt = 0;
      }
      newData.prov_13 = salario * 0.0833;
      newData.prov_ferias = salario * 0.1111;
      newData.fgts = salario * 0.08;
    } else if (salario > 0) {
      // If salary is filled but no regime selected, show preview calculations
      // Only calculate desconto_vt if VT > 0
      if (vt > 0) {
        newData.desconto_vt = Math.min(vt * 22, salario * 0.06);
      } else {
        newData.desconto_vt = 0;
      }
      newData.prov_13 = salario * 0.0833;
      newData.prov_ferias = salario * 0.1111;
      newData.fgts = salario * 0.08;
      if (!newData.nf_13_manual_edit) {
        newData.nf_13 = salario / 12;
      }
    }

    // Calculate VT CUSTO for both regimes + taxa_vt + repasse_vt
    const taxa_vt = parseFloat(newData.taxa_vt) || 0;
    const repasse_vt = parseFloat(newData.repasse_vt) || 0;
    if (vt > 0) {
      const desconto_vt = parseFloat(newData.desconto_vt) || 0;
      newData.vt_custo = Math.max(0, vt * 22 - desconto_vt) + taxa_vt + repasse_vt;
    } else {
      newData.vt_custo = taxa_vt + repasse_vt;
    }

    // Helper function to get numeric value from selection fields - with safety checks
    const getSelectionValue = (fieldName) => {
      try {
        const selectedValue = newData[fieldName];
        if (!selectedValue || selectedValue === 'Nenhuma') return 0;

        // Don't try to access options if they're not loaded yet
        if (!options || typeof options !== 'object') return 0;

        // Find the option value based on selection
        const optionMap = {
          'odonto': 'odontoOptions',
          'seg_vida': 'segVidaOptions',
          'total_pass': 'totalPassOptions',
          'med_ocupacional': 'medOcupacionalOptions'
        };

        const optionKey = optionMap[fieldName];
        if (!optionKey || !options[optionKey] || !Array.isArray(options[optionKey])) {
          return 0;
        }

        const option = options[optionKey].find(opt => opt && opt.name === selectedValue);
        return option ? (parseFloat(option.value) || 0) : 0;
      } catch (error) {
        console.warn('Error getting selection value for', fieldName, ':', error);
        return 0;
      }
    };

    // Calcular total dos beneficios_outros (array dinâmico)
    const outrosTotal = Array.isArray(newData.beneficios_outros)
      ? newData.beneficios_outros.reduce((sum, item) => sum + (parseFloat(item.valor) || 0), 0)
      : 0;
    newData.beneficios_outros_valor = outrosTotal;

    // Calculate TOTAL (sum all numeric fields except VT, DESCONTO VT (-), FGTS)
    const totalFields = [
      'salario', 'acumulo_funcao', 'vt_custo', 'va', 'vr', 'aux_combustivel',
      'hora_extra', 'comissao', 'ajuda_custo',
      'auxilio_creche', 'segunda_refeicao', 'pernoite', 'assist_medica',
      'nf_13', 'prov_13', 'prov_ferias', 'inss', 'beneficios_outros_valor'
    ];

    const total = totalFields.reduce((sum, field) => {
      const value = parseFloat(newData[field]) || 0;
      return sum + value;
    }, 0);

    // Add selection field values to total - with safety checks
    const selectionTotal = getSelectionValue('odonto') +
      getSelectionValue('seg_vida') +
      getSelectionValue('total_pass') +
      getSelectionValue('med_ocupacional');

    newData.total = total + selectionTotal;

    return newData;
  };

  const handleInputChange = (field, value) => {
    try {
      setFormData(prev => {
        const newData = {
          ...prev,
          [field]: value
        };

        // Mark manual edit for nf_13 field
        if (field === 'nf_13') {
          newData.nf_13_manual_edit = true;
        }
        if (field === 'possui_decimo_terceiro') {
          newData.nf_13_manual_edit = false;
        }
        // Regra específica: Se VT = 0, então VT Custo e Desconto VT também devem ser 0
        if (field === 'vt' && (value === 0 || value === '0' || !value)) {
          newData.vt_custo = (parseFloat(newData.taxa_vt) || 0) + (parseFloat(newData.repasse_vt) || 0);
          newData.desconto_vt = 0;
        }

        // Cálculo inverso: quando VT Custo é editado, recalcula VT(dia) 
        if (field === 'vt_custo') {
          const salario = parseFloat(prev.salario) || 0;
          const vtCusto = parseFloat(value) || 0;
          newData.vt = Math.max(0, (vtCusto + salario * 0.06) / 22);
        }

        // Only recalculate for financial/calculation fields, not personal/address fields
        const financialFields = [
          'salario', 'regime', 'vt', 'vt_custo', 'taxa_vt', 'repasse_vt', 'va', 'vr', 'acumulo_funcao', 'aux_combustivel',
          'hora_extra', 'comissao', 'ajuda_custo', 'auxilio_creche', 'segunda_refeicao',
          'pernoite', 'assist_medica', 'nf_13', 'prov_13', 'prov_ferias', 'fgts', 'inss',
          'odonto', 'seg_vida', 'total_pass', 'med_ocupacional', 'beneficios_outros_valor'
        ];

        if (financialFields.includes(field)) {
          return calculateEmployeeFields(newData);
        } else {
          // For non-financial fields, just return the updated data without recalculation
          return newData;
        }
      });
    } catch (error) {
      console.error('Error in handleInputChange:', error);
      // If calculation fails, just update the field without recalculating
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor, selecione um arquivo de imagem válido');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('A imagem deve ter no máximo 5MB');
        return;
      }

      // eslint-disable-next-line no-undef
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          foto: e.target.result, // base64 string
          foto_file: file
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCepLookup = async (cep) => {
    const cepDigits = onlyNumbers(cep || '');
    if (cepDigits.length === 8) {
      try {
        const response = await axios.get(`${API}/api/cep/${cep}`);
        const addressData = response.data;

        if (addressData) {
          setFormData(prev => ({
            ...prev,
            logradouro: addressData.logradouro || prev.logradouro,
            bairro: addressData.bairro || prev.bairro,
            cidade: addressData.cidade || prev.cidade,
            estado: addressData.estado || prev.estado
          }));
          toast.success('CEP encontrado e endereço preenchido!');
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
        toast.error('CEP não encontrado');
      }
    }
  };

  const handleSalaryLookup = async (cargoName, faixa) => {
    if (!cargoName || !faixa) return;

    try {
      const response = await axios.get(`${API}/api/salary-lookup/${encodeURIComponent(cargoName)}/${encodeURIComponent(faixa)}`);
      if (response.data && response.data.found && response.data.salary) {
        handleInputChange('salario', response.data.salary);
        toast.success(`Salário preenchido automaticamente: ${formatCurrency(response.data.salary)}`);
      } else {
        toast.info('Salário não encontrado para este cargo e faixa');
      }
    } catch (error) {
      console.error('Erro ao buscar salário:', error);
      toast.info('Erro ao buscar salário para este cargo e faixa');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Geolocation functions
  const calculateGeolocation = async () => {
    // Check required fields for address construction
    if (!formData.logradouro || !formData.cidade || !formData.estado) {
      toast.error('Preencha pelo menos o Logradouro, Cidade e Estado do funcionário primeiro');
      return;
    }

    setGeolocationLoading(true);

    try {
      // Build complete address from form data
      const origemEndereco = `${formData.logradouro}, ${formData.numero || 'S/N'}, ${formData.bairro || ''}, ${formData.cidade}, ${formData.estado}, CEP: ${formData.cep}`.trim();

      // Send as query parameters, not JSON body
      const params = {
        origem_endereco: origemEndereco,
        destino_endereco: defaultAddress?.endereco_completo || null
      };

      const response = await axios.post(`${API}/api/geolocation/calculate`, null, {
        params: params
      });

      setGeolocationData(response.data);

      // Salvar dados de geolocalização no formData para persistir no banco
      setFormData(prev => ({
        ...prev,
        geolocation_origem: origemEndereco,
        geolocation_destino: defaultAddress?.endereco_completo || '',
      }));

      toast.success('Informações de geolocalização calculadas e salvas com sucesso!');

    } catch (error) {
      console.error('Erro ao calcular geolocalização:', error);
      if (error.response?.data?.detail) {
        toast.error(`Erro: ${error.response.data.detail}`);
      } else {
        toast.error('Erro ao calcular informações de geolocalização');
      }
    } finally {
      setGeolocationLoading(false);
    }
  };

  // Helper function to check if employee address is complete enough
  const hasValidEmployeeAddress = () => {
    return !!(formData.logradouro && formData.cidade && formData.estado);
  };

  // Helper function to build complete employee address for display
  const getCompleteEmployeeAddress = () => {
    if (!formData.logradouro) return 'Endereço não informado';

    const parts = [];
    if (formData.logradouro) parts.push(formData.logradouro);
    if (formData.numero) parts.push(formData.numero);
    if (formData.bairro) parts.push(formData.bairro);
    if (formData.cidade) parts.push(formData.cidade);
    if (formData.estado) parts.push(formData.estado);
    if (formData.cep) parts.push(`CEP: ${formData.cep}`);

    return parts.join(', ');
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    return timeStr;
  };

  const formatDistance = (distanceStr) => {
    if (!distanceStr) return 'N/A';
    return distanceStr;
  };

  const addDependent = () => {
    setFormData(prev => ({
      ...prev,
      dependentes: [...(prev.dependentes || []), {
        nome: '',
        cpf: '',
        data_nascimento: '',
        parentesco: '',
        imposto_renda: false,
        atipico: false,
        beneficios: []
      }]
    }));
  };

  const removeDependent = (index) => {
    setFormData(prev => ({
      ...prev,
      dependentes: (prev.dependentes || []).filter((_, i) => i !== index)
    }));
  };

  const updateDependent = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      dependentes: (prev.dependentes || []).map((dep, i) =>
        i === index ? { ...dep, [field]: value } : dep
      )
    }));
  };

  const addDependentBenefit = (dependenteIndex) => {
    const dependente = (formData.dependentes || [])[dependenteIndex] || {};
    updateDependent(dependenteIndex, 'beneficios', [
      ...(dependente.beneficios || []),
      {
        tipo: 'convenio_medico',
        descricao: '',
        valor: '',
        ativo: true,
        deduzir_folha: false,
        competencias: ['folha_mensal'],
      },
    ]);
  };

  const removeDependentBenefit = (dependenteIndex, beneficioIndex) => {
    const dependente = (formData.dependentes || [])[dependenteIndex] || {};
    updateDependent(dependenteIndex, 'beneficios', (dependente.beneficios || []).filter((_, i) => i !== beneficioIndex));
  };

  const updateDependentBenefit = (dependenteIndex, beneficioIndex, field, value) => {
    const dependente = (formData.dependentes || [])[dependenteIndex] || {};
    const nextValue = field === 'valor' ? formatDependentBenefitCurrency(value) : value;
    const beneficios = (dependente.beneficios || []).map((beneficio, i) =>
      i === beneficioIndex ? { ...beneficio, [field]: nextValue } : beneficio
    );
    updateDependent(dependenteIndex, 'beneficios', beneficios);
  };

  const addBeneficiario = () => {
    setFormData(prev => ({
      ...prev,
      beneficiarios: [...(prev.beneficiarios || []), {
        nome: '',
        cpf: '',
        rg: '',
        data_nascimento: '',
        tipo_beneficio: '',
        forma_recebimento: 'banco',
        banco: '',
        agencia: '',
        conta: '',
        chave_pix: '',
        tipos_competencia: [],
        dependentes_vinculados: []
      }]
    }));
  };

  const removeBeneficiario = (index) => {
    setFormData(prev => ({
      ...prev,
      beneficiarios: (prev.beneficiarios || []).filter((_, i) => i !== index)
    }));
  };

  const updateBeneficiario = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      beneficiarios: (prev.beneficiarios || []).map((ben, i) =>
        i === index ? { ...ben, [field]: value } : ben
      )
    }));
  };

  const handleLMSCourseToggle = async (courseId, isAssigned) => {
    if (!id) return;

    try {
      if (isAssigned) {
        // Desatribuir curso
        await axios.delete(`${API}/api/employees/${id}/lms-courses/${courseId}`);
        toast.success('Curso removido com sucesso!');
      } else {
        // Atribuir curso
        await axios.post(`${API}/api/employees/${id}/lms-courses`, {
          course_id: courseId,
          assigned_by: 'admin',
          notes: 'Atribuído via edição de funcionário'
        });
        toast.success('Curso atribuído com sucesso!');
      }

      // Recarregar dados do Grid Learning
      await fetchLMSData();
    } catch (error) {
      console.error('Erro ao atualizar curso:', error);
      toast.error('Erro ao atualizar curso');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validação de campos
    if (formData.cpf) {
      const cpfDigits = onlyNumbers(formData.cpf);
      if (cpfDigits.length === 11 && !validateCPF(cpfDigits)) {
        toast.error('CPF inválido');
        setLoading(false);
        return;
      }
    }

    if (formData.telefone) {
      const telDigits = onlyNumbers(formData.telefone);
      if (telDigits.length >= 10 && !validatePhone(telDigits)) {
        toast.error('Telefone celular inválido. Use o formato (11) 99999-9999');
        setLoading(false);
        return;
      }
    }

    if (formData.contato_emergencia_telefone) {
      const telDigits = onlyNumbers(formData.contato_emergencia_telefone);
      if (telDigits.length >= 10 && !validatePhone(telDigits)) {
        toast.error('Telefone do contato de emergência inválido. Use o formato (11) 99999-9999');
        setLoading(false);
        return;
      }
    }

    if (formData.cep && !validateCEP(formData.cep)) {
      toast.error('CEP inválido. Deve conter 8 dígitos');
      setLoading(false);
      return;
    }

    if (formData.data_nascimento && !validateDateBR(formData.data_nascimento, { futureYears: 0, pastYears: 120 })) {
      toast.error('Data de nascimento inválida');
      setLoading(false);
      return;
    }

    if (formData.admissao && !validateDateBR(formData.admissao, { pastYears: 15, futureYears: 15 })) {
      toast.error('Data de admissão inválida');
      setLoading(false);
      return;
    }

    if (formData.data_demissao && !validateDateBR(formData.data_demissao, { futureYears: 0, pastYears: 15 })) {
      toast.error('Data de demissão inválida');
      setLoading(false);
      return;
    }

    if (formData.ctps_data_emissao && !validateDateBR(formData.ctps_data_emissao, { futureYears: 0, pastYears: 15 })) {
      toast.error('Data de emissão da CTPS inválida');
      setLoading(false);
      return;
    }

    if (formData.cnh_validade && !validateDateBR(formData.cnh_validade, { pastYears: 0, futureYears: 15 })) {
      toast.error('Data de validade da CNH inválida');
      setLoading(false);
      return;
    }

    if (!formData.adiantamento) {
      toast.error('Selecione se o funcionário opta por adiantamento salarial');
      setLoading(false);
      return;
    }

    const beneficioDependenteInvalido = (formData.dependentes || []).some((dependente) =>
      (dependente.beneficios || []).some((beneficio) =>
        beneficio.deduzir_folha &&
        (!(parseDependentBenefitCurrency(beneficio.valor) > 0) || !Array.isArray(beneficio.competencias) || beneficio.competencias.length === 0)
      )
    );
    if (beneficioDependenteInvalido) {
      toast.error('Informe um valor maior que zero e ao menos uma competência para cada benefício com desconto em folha');
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const endpoint = isEditing ? `${API}/api/employees-detailed/${id}` : `${API}/api/employees-detailed`;
      const method = isEditing ? 'PUT' : 'POST';

      // Garantir que company_id seja string (não objeto)
      const cleanedData = {
        ...formData,
        dependentes: (formData.dependentes || []).map(dependente => ({
          ...dependente,
          beneficios: (dependente.beneficios || []).map(beneficio => ({
            ...beneficio,
            valor: parseDependentBenefitCurrency(beneficio.valor),
          })),
        })),
        cpf: onlyNumbers(formData.cpf),
        cep: onlyNumbers(formData.cep),
        telefone: onlyNumbers(formData.telefone),
        contato_emergencia_telefone: onlyNumbers(formData.contato_emergencia_telefone),
        company_id: typeof formData.company_id === 'object' ? formData.company_id?.id || '' : formData.company_id
      };

      const employeeResponse = await axios({
        method,
        url: endpoint,
        data: cleanedData,
        headers: { Authorization: `Bearer ${token}` }
      });

      const savedEmployeeId = employeeResponse?.data?.id || id;
      if (savedEmployeeId) {
        let existingDpData = {};
        try {
          const dpResponse = await axios.get(`${API}/api/calculos-dp/employees/${savedEmployeeId}/dp-data`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          existingDpData = dpResponse?.data?.dp_data || {};
        } catch (dpError) {
          existingDpData = {};
        }

        await axios.put(
          `${API}/api/calculos-dp/employees/${savedEmployeeId}/dp-data`,
          {
            ...existingDpData,
            optante_sindicato: !!formData.optante_sindicato
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success(isEditing ? 'Funcionário atualizado com sucesso!' : 'Funcionário cadastrado com sucesso!');
      navigate(returnPath);
    } catch (error) {
      console.error('Erro ao salvar funcionário:', error);

      // Tratamento especial para erros de validação Pydantic
      let errorMessage = 'Erro ao salvar funcionário';

      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;

        // Se for array de erros de validação Pydantic
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(err => {
            const field = err.loc ? err.loc.join(' → ') : 'Campo';
            return `${field}: ${err.msg}`;
          }).join('; ');
          errorMessage = errorMessages;
        }
        // Se for string simples
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Se for objeto com mensagem
        else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        }
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while data is not ready
  if (loading || !dataLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  const evaluationSummaryLoaded = !!loadedResources.employeeEvaluationData;
  const lmsSummaryLoaded = !!loadedResources.lmsData;
  const isInitialDataLoading = isEditing && !dataLoaded;

  const renderInitialTabLoadingState = () => (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle>{TAB_LABELS[activeTab] || 'Carregando aba'}</CardTitle>
            <CardDescription>Buscando os dados do colaborador para preencher esta seção.</CardDescription>
          </div>
          <div className="flex items-center text-sm text-blue-600">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Carregando
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-pulse">
          <div className="space-y-2">
            <div className="h-4 w-24 rounded bg-slate-200" />
            <div className="h-10 w-full rounded-md bg-slate-100" />
          </div>
          <div className="space-y-2">
            <div className="h-4 w-32 rounded bg-slate-200" />
            <div className="h-10 w-full rounded-md bg-slate-100" />
          </div>
          <div className="space-y-2">
            <div className="h-4 w-20 rounded bg-slate-200" />
            <div className="h-10 w-full rounded-md bg-slate-100" />
          </div>
          <div className="space-y-2">
            <div className="h-4 w-28 rounded bg-slate-200" />
            <div className="h-10 w-full rounded-md bg-slate-100" />
          </div>
        </div>
        <div className="space-y-3 animate-pulse">
          <div className="h-4 w-40 rounded bg-slate-200" />
          <div className="h-24 w-full rounded-lg bg-slate-100" />
          <div className="h-24 w-full rounded-lg bg-slate-100" />
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">
            {isEditing ? 'Editar Funcionário' : 'Novo Funcionário'}
          </h1>
          <p className="text-slate-600 mt-1">
            {isEditing ? 'Atualize as informações do funcionário' : 'Complete todas as informações do funcionário'}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(returnPath)}
        >
          Voltar
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar de Navegação */}
          <div className="w-full lg:w-64 lg:flex-shrink-0">
            <div className="lg:sticky lg:top-6">
              <TabsList className="flex flex-row lg:flex-col h-auto bg-white border border-slate-200 rounded-lg p-2 gap-1 space-y-0 lg:space-y-1 shadow-sm overflow-x-auto lg:overflow-x-visible">
                <TabsTrigger value="personal" className="flex-shrink-0 lg:w-full justify-start px-4 py-3 whitespace-nowrap data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                  <User className="h-4 w-4 mr-3" />
                  Dados Pessoais
                </TabsTrigger>
                <TabsTrigger value="contractual" className="flex-shrink-0 lg:w-full justify-start px-4 py-3 whitespace-nowrap data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                  <Building2 className="h-4 w-4 mr-3" />
                  Contratuais
                </TabsTrigger>
                <TabsTrigger value="banking" className="flex-shrink-0 lg:w-full justify-start px-4 py-3 whitespace-nowrap data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                  <CreditCard className="h-4 w-4 mr-3" />
                  Bancários
                </TabsTrigger>
                <TabsTrigger value="dependents" className="flex-shrink-0 lg:w-full justify-start px-4 py-3 whitespace-nowrap data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                  <Users className="h-4 w-4 mr-3" />
                  Dependentes
                </TabsTrigger>
                <TabsTrigger value="beneficiarios" className="flex-shrink-0 lg:w-full justify-start px-4 py-3 whitespace-nowrap data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                  <Heart className="h-4 w-4 mr-3" />
                  Beneficiários
                </TabsTrigger>
                <TabsTrigger value="documents" className="flex-shrink-0 lg:w-full justify-start px-4 py-3 whitespace-nowrap data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                  <FileText className="h-4 w-4 mr-3" />
                  Documentos
                </TabsTrigger>
                <TabsTrigger value="benefits" className="flex-shrink-0 lg:w-full justify-start px-4 py-3 whitespace-nowrap data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                  <Gift className="h-4 w-4 mr-3" />
                  Benefícios
                </TabsTrigger>
                <TabsTrigger value="calculations" className="flex-shrink-0 lg:w-full justify-start px-4 py-3 whitespace-nowrap data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                  <Calculator className="h-4 w-4 mr-3" />
                  Cálculos
                </TabsTrigger>
                <TabsTrigger value="geolocation" className="flex-shrink-0 lg:w-full justify-start px-4 py-3 whitespace-nowrap data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                  <MapPin className="h-4 w-4 mr-3" />
                  Geolocalização
                </TabsTrigger>
                <TabsTrigger value="escolaridade" className="flex-shrink-0 lg:w-full justify-start px-4 py-3 whitespace-nowrap data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                  <GraduationCap className="h-4 w-4 mr-3" />
                  Escolaridade
                </TabsTrigger>
                <TabsTrigger value="lms" className="flex-shrink-0 lg:w-full justify-start px-4 py-3 whitespace-nowrap data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                  <BookOpen className="h-4 w-4 mr-3" />
                  Grid Learning
                </TabsTrigger>
                <TabsTrigger value="jornadas" className="flex-shrink-0 lg:w-full justify-start px-4 py-3 whitespace-nowrap data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                  <Clock className="h-4 w-4 mr-3" />
                  Jornadas
                </TabsTrigger>
                <TabsTrigger value="feedback" className="flex-shrink-0 lg:w-full justify-start px-4 py-3 whitespace-nowrap data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                  <Target className="h-4 w-4 mr-3" />
                  Feedback
                </TabsTrigger>
                <TabsTrigger value="dcf" className="flex-shrink-0 lg:w-full justify-start px-4 py-3 whitespace-nowrap data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                  <Briefcase className="h-4 w-4 mr-3" />
                  DCF
                </TabsTrigger>
                <TabsTrigger value="ponto" className="flex-shrink-0 lg:w-full justify-start px-4 py-3 whitespace-nowrap data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
                  <Smartphone className="h-4 w-4 mr-3" />
                  Acesso Ponto
                </TabsTrigger>
              </TabsList>
            </div>
          </div>

          {/* Conteúdo das Abas */}
          <div className="flex-1 min-w-0">
            {isInitialDataLoading ? (
              renderInitialTabLoadingState()
            ) : (
              <>

            {/* Personal Data Tab */}
            <TabsContent value="personal">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <User className="h-5 w-5" />
                    <span>Informações Pessoais</span>
                  </CardTitle>
                  <CardDescription>
                    Dados pessoais e de identificação do funcionário
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Photo and Employee Info Section */}
                  <div className="flex flex-col lg:flex-row gap-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                    {/* Photo Section - Left Side */}
                    <div className="flex-shrink-0">
                      <div className="flex flex-col items-center space-y-4">
                        <div className="relative">
                          {formData.foto ? (
                            <img
                              src={formData.foto}
                              alt="Foto do funcionário"
                              className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                            />
                          ) : (
                            <div className="w-32 h-32 rounded-full bg-gray-200 border-4 border-white shadow-lg flex items-center justify-center">
                              <User className="h-16 w-16 text-gray-400" />
                            </div>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoUpload}
                            className="hidden"
                            id="photo-upload"
                          />
                          <label
                            htmlFor="photo-upload"
                            className="absolute bottom-0 right-0 bg-blue-500 hover:bg-blue-600 text-white rounded-full p-2 cursor-pointer shadow-lg transition-colors"
                          >
                            <Upload className="h-4 w-4" />
                          </label>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-700">Foto do Funcionário</p>
                          <p className="text-xs text-gray-500">Clique no ícone para alterar</p>
                        </div>

                        {/* Informações do Colaborador */}
                        {isEditing && formData.nome && (
                          <div className="w-full mt-4 space-y-2 text-sm">
                            <div className="bg-white rounded-lg p-3 shadow-sm">
                              <p className="text-xs text-gray-500 mb-1">Cargo</p>
                              <p className="font-medium text-gray-800">{formData.cargo || 'Não informado'}</p>
                            </div>

                            <div className="bg-white rounded-lg p-3 shadow-sm">
                              <p className="text-xs text-gray-500 mb-1">Data de Admissão</p>
                              <p className="font-medium text-gray-800">
                                {formatDateBR(formData.admissao)}
                              </p>
                              {formData.admissao && (
                                <p className="text-xs text-indigo-600 mt-1">
                                  {calculateTenure(formData.admissao)}
                                </p>
                              )}
                            </div>

                            <div className="bg-white rounded-lg p-3 shadow-sm">
                              <p className="text-xs text-gray-500 mb-1">Data de Nascimento</p>
                              <p className="font-medium text-gray-800">
                                {formatDateBR(formData.data_nascimento)}
                              </p>
                              {formData.data_nascimento && (
                                <p className="text-xs text-indigo-600 mt-1">
                                  {calculateAge(formData.data_nascimento)} anos
                                </p>
                              )}
                            </div>

                            <div className="bg-white rounded-lg p-3 shadow-sm">
                              <p className="text-xs text-gray-500 mb-1">Situação</p>
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${formData.situacao === 'Ativo' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                <p className={`font-medium ${formData.situacao === 'Ativo' ? 'text-green-700' : 'text-red-700'}`}>
                                  {formData.situacao || 'Não informado'}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Employee Info Section - Right Side */}
                    {isEditing && (
                      <div className="flex-1 space-y-4">
                        <div className="flex items-start justify-between gap-4 mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-800">Resumo das Avaliações</h3>
                            {(!evaluationSummaryLoaded || !lmsSummaryLoaded) && (
                              <p className="text-xs text-gray-500 mt-1">
                                Indicadores avançados são carregados sob demanda nas abas `Feedback` e `Grid Learning`.
                              </p>
                            )}
                          </div>
                          {(resourceLoading.employeeEvaluationData || resourceLoading.lmsData) && (
                            <div className="flex items-center text-xs text-blue-600">
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Atualizando resumo
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* DISC Assessment */}
                          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${getDISCBadgeInfo(employeeEvaluationData.discAssessment).color}`}>
                                {getDISCBadgeInfo(employeeEvaluationData.discAssessment).letter}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-800">Avaliação DISC</p>
                                <p className="text-xs text-gray-600">{getDISCBadgeInfo(employeeEvaluationData.discAssessment).label}</p>
                              </div>
                            </div>
                            {employeeEvaluationData.discAssessment && (
                              <div className="mt-2 space-y-1">
                                {Object.entries(getDISCBadgeInfo(employeeEvaluationData.discAssessment).scores).map(([key, value]) => (
                                  <div key={key} className="flex justify-between text-xs">
                                    <span className="text-gray-600">{key}:</span>
                                    <span className="font-medium">{value?.toFixed(1)}%</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* PDI */}
                          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                <FileText className="h-4 w-4 text-purple-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-800">PDI</p>
                                <p className={`text-xs ${getPDIInfo(employeeEvaluationData.pdiData).color}`}>
                                  {getPDIInfo(employeeEvaluationData.pdiData).status}
                                </p>
                              </div>
                            </div>
                            {employeeEvaluationData.pdiData && (
                              <div className="mt-2">
                                <p className="text-xs text-gray-600 mb-1">{getPDIInfo(employeeEvaluationData.pdiData).title}</p>
                                <p className="text-xs text-gray-500">{getPDIInfo(employeeEvaluationData.pdiData).goals} metas</p>
                              </div>
                            )}
                          </div>

                          {/* Grid Learning Courses */}
                          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                            <div className="flex items-center space-x-3 mb-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center">
                                <span className="text-green-600 font-bold text-base">{employeeEvaluationData.lmsCoursesData?.total || 0}</span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-800">Grid Learning</p>
                                <p className="text-xs text-gray-600">Total de cursos</p>
                              </div>
                            </div>
                            <div className="space-y-1 mt-2 pt-2 border-t border-gray-100">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-600 flex items-center">
                                  <span className="w-2 h-2 rounded-full bg-green-500 mr-1.5"></span>
                                  Concluídos
                                </span>
                                <span className="font-semibold text-green-600">{employeeEvaluationData.lmsCoursesData?.completed || 0}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-600 flex items-center">
                                  <span className="w-2 h-2 rounded-full bg-blue-500 mr-1.5"></span>
                                  Em andamento
                                </span>
                                <span className="font-semibold text-blue-600">{employeeEvaluationData.lmsCoursesData?.inProgress || 0}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-600 flex items-center">
                                  <span className="w-2 h-2 rounded-full bg-gray-400 mr-1.5"></span>
                                  Não iniciados
                                </span>
                                <span className="font-semibold text-gray-600">{employeeEvaluationData.lmsCoursesData?.notStarted || 0}</span>
                              </div>
                            </div>
                          </div>

                          {/* Performance Evaluation */}
                          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <Calculator className="h-4 w-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-800">Desempenho</p>
                                <p className={`text-xs ${getPerformanceInfo(employeeEvaluationData.performanceEvaluation).color}`}>
                                  {getPerformanceInfo(employeeEvaluationData.performanceEvaluation).summary}
                                </p>
                              </div>
                            </div>
                            {employeeEvaluationData.performanceEvaluation && (
                              <div className="mt-2">
                                <p className="text-xs text-gray-600 mb-1">
                                  Score: {getPerformanceInfo(employeeEvaluationData.performanceEvaluation).score}%
                                </p>
                                <p className="text-xs text-gray-500">
                                  {getPerformanceInfo(employeeEvaluationData.performanceEvaluation).date}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* 9Box Position */}
                          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 md:col-span-2">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${getNineBoxInfo(employeeEvaluationData.nineBoxPosition).color.split(' ')[0]} ${getNineBoxInfo(employeeEvaluationData.nineBoxPosition).color.split(' ')[1]}`}>
                                {getNineBoxInfo(employeeEvaluationData.nineBoxPosition).icon}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-800">9-Box</p>
                                <p className={`text-xs ${getNineBoxInfo(employeeEvaluationData.nineBoxPosition).color.split(' ')[0]}`}>
                                  {getNineBoxInfo(employeeEvaluationData.nineBoxPosition).status}
                                </p>
                              </div>
                            </div>
                            {employeeEvaluationData.nineBoxPosition && (
                              <div className="mt-2 flex space-x-4">
                                <p className="text-xs text-gray-600">
                                  Performance: {getNineBoxInfo(employeeEvaluationData.nineBoxPosition).performance}
                                </p>
                                <p className="text-xs text-gray-600">
                                  Potencial: {getNineBoxInfo(employeeEvaluationData.nineBoxPosition).potential}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nome">Nome Completo</Label>
                      <Input
                        id="nome"
                        value={formData.nome}
                        onChange={(e) => handleInputChange('nome', e.target.value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s'-]/g, ''))}
                        placeholder="Nome completo do funcionário"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cpf">CPF</Label>
                      <Input
                        id="cpf"
                        value={formData.cpf}
                        onChange={(e) => handleInputChange('cpf', formatCPF(e.target.value))}
                        placeholder="000.000.000-00"
                        maxLength={14}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="rg_numero">RG</Label>
                      <Input
                        id="rg_numero"
                        value={formData.rg_numero}
                        onChange={(e) => handleInputChange('rg_numero', formatRG(e.target.value))}
                        placeholder="12.345.678-9"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rg_orgao">Órgão Emissor</Label>
                      <Input
                        id="rg_orgao"
                        value={formData.rg_orgao}
                        onChange={(e) => handleInputChange('rg_orgao', e.target.value)}
                        placeholder="SSP"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rg_uf">UF</Label>
                      <Input
                        id="rg_uf"
                        value={formData.rg_uf}
                        onChange={(e) => handleInputChange('rg_uf', e.target.value)}
                        placeholder="SP"
                        maxLength={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="data_nascimento">Data de Nascimento</Label>
                      <Input
                        id="data_nascimento"
                        type="date"
                        value={formData.data_nascimento}
                        onChange={(e) => handleInputChange('data_nascimento', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="sexo">Sexo</Label>
                      <Select
                        value={formData.sexo}
                        onValueChange={(value) => handleInputChange('sexo', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="masculino">Masculino</SelectItem>
                          <SelectItem value="feminino">Feminino</SelectItem>
                          <SelectItem value="outro">Outro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nacionalidade">Nacionalidade</Label>
                      <Input
                        id="nacionalidade"
                        value={formData.nacionalidade}
                        onChange={(e) => handleInputChange('nacionalidade', e.target.value)}
                        placeholder="Brasileira"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="naturalidade">Naturalidade</Label>
                      <Input
                        id="naturalidade"
                        value={formData.naturalidade}
                        onChange={(e) => handleInputChange('naturalidade', e.target.value)}
                        placeholder="São Paulo/SP"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="estado_civil">Estado Civil</Label>
                      <Select
                        value={formData.estado_civil}
                        onValueChange={(value) => handleInputChange('estado_civil', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="solteiro">Solteiro(a)</SelectItem>
                          <SelectItem value="casado">Casado(a)</SelectItem>
                          <SelectItem value="separado">Separado(a)</SelectItem>
                          <SelectItem value="divorciado">Divorciado(a)</SelectItem>
                          <SelectItem value="viuvo">Viúvo(a)</SelectItem>
                          <SelectItem value="uniao_estavel">União Estável</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="etnia_id">Etnia</Label>
                      <Select
                        value={formData.etnia_id}
                        onValueChange={(value) => handleInputChange('etnia_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a etnia" />
                        </SelectTrigger>
                        <SelectContent>
                          {etnias.map((etnia) => (
                            <SelectItem key={etnia.id} value={etnia.id}>
                              {etnia.nome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nome_mae">Nome da Mãe</Label>
                      <Input
                        id="nome_mae"
                        value={formData.nome_mae}
                        onChange={(e) => handleInputChange('nome_mae', e.target.value.replace(/\d/g, ''))}
                        placeholder="Nome completo da mãe"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nome_pai">Nome do Pai</Label>
                      <Input
                        id="nome_pai"
                        value={formData.nome_pai}
                        onChange={(e) => handleInputChange('nome_pai', e.target.value.replace(/\d/g, ''))}
                        placeholder="Nome completo do pai"
                      />
                    </div>
                  </div>

                  {/* Documentos Adicionais */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Documentos Adicionais</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="pis_pasep_nit">PIS/PASEP/NIT</Label>
                        <Input
                          id="pis_pasep_nit"
                          value={formData.pis_pasep_nit}
                          onChange={(e) => handleInputChange('pis_pasep_nit', e.target.value)}
                          placeholder="000.00000.00-0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="certificado_reservista">Certificado de Reservista</Label>
                        <Input
                          id="certificado_reservista"
                          value={formData.certificado_reservista}
                          onChange={(e) => handleInputChange('certificado_reservista', e.target.value)}
                          placeholder="Número do certificado"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="titulo_eleitor_numero">Título de Eleitor</Label>
                        <Input
                          id="titulo_eleitor_numero"
                          value={formData.titulo_eleitor_numero}
                          onChange={(e) => handleInputChange('titulo_eleitor_numero', e.target.value)}
                          placeholder="0000 0000 0000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="titulo_eleitor_zona">Zona</Label>
                        <Input
                          id="titulo_eleitor_zona"
                          value={formData.titulo_eleitor_zona}
                          onChange={(e) => handleInputChange('titulo_eleitor_zona', e.target.value)}
                          placeholder="000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="titulo_eleitor_secao">Seção</Label>
                        <Input
                          id="titulo_eleitor_secao"
                          value={formData.titulo_eleitor_secao}
                          onChange={(e) => handleInputChange('titulo_eleitor_secao', e.target.value)}
                          placeholder="0000"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Address Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Endereço</h3>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="cep">CEP</Label>
                        <div className="flex space-x-2">
                          <Input
                            id="cep"
                            value={formData.cep}
                            onChange={(e) => handleInputChange('cep', formatCEP(e.target.value))}
                            placeholder="00000-000"
                            maxLength={9}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleCepLookup(formData.cep || '')}
                            disabled={!formData.cep || onlyNumbers(formData.cep).length !== 8}
                          >
                            <Search className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label htmlFor="endereco">Logradouro</Label>
                        <Input
                          id="logradouro"
                          value={formData.logradouro}
                          onChange={(e) => handleInputChange('logradouro', e.target.value)}
                          placeholder="Rua, Avenida, etc."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="numero">Número</Label>
                        <Input
                          id="numero"
                          value={formData.numero}
                          onChange={(e) => handleInputChange('numero', e.target.value)}
                          placeholder="123"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="complemento">Complemento</Label>
                        <Input
                          id="complemento"
                          value={formData.complemento}
                          onChange={(e) => handleInputChange('complemento', e.target.value)}
                          placeholder="Apto, Casa, etc."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bairro">Bairro</Label>
                        <Input
                          id="bairro"
                          value={formData.bairro}
                          onChange={(e) => handleInputChange('bairro', e.target.value)}
                          placeholder="Nome do bairro"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cidade">Cidade</Label>
                        <Input
                          id="cidade"
                          value={formData.cidade}
                          onChange={(e) => handleInputChange('cidade', e.target.value)}
                          placeholder="Cidade"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="estado">Estado</Label>
                        <Input
                          id="estado"
                          value={formData.estado}
                          onChange={(e) => handleInputChange('estado', e.target.value)}
                          placeholder="UF"
                          maxLength={2}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="telefone">Telefone Celular</Label>
                      <Input
                        id="telefone"
                        value={formData.telefone}
                        onChange={(e) => handleInputChange('telefone', formatPhone(e.target.value))}
                        placeholder="(11) 99999-9999"
                        maxLength={16}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="email@exemplo.com"
                      />
                    </div>
                  </div>

                  {/* CTPS Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">CTPS - Carteira de Trabalho</h3>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="ctps_numero">Número</Label>
                        <Input
                          id="ctps_numero"
                          value={formData.ctps_numero}
                          onChange={(e) => handleInputChange('ctps_numero', e.target.value)}
                          placeholder="1234567"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ctps_serie">Série</Label>
                        <Input
                          id="ctps_serie"
                          value={formData.ctps_serie}
                          onChange={(e) => handleInputChange('ctps_serie', e.target.value)}
                          placeholder="123"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ctps_data_emissao">Data de Emissão</Label>
                        <Input
                          id="ctps_data_emissao"
                          type="date"
                          value={formData.ctps_data_emissao}
                          onChange={(e) => handleInputChange('ctps_data_emissao', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ctps_uf">UF</Label>
                        <Input
                          id="ctps_uf"
                          value={formData.ctps_uf}
                          onChange={(e) => handleInputChange('ctps_uf', e.target.value)}
                          placeholder="SP"
                          maxLength={2}
                        />
                      </div>
                    </div>
                  </div>

                  {/* CNH Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">CNH - Carteira Nacional de Habilitação</h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="cnh_numero">Número</Label>
                        <Input
                          id="cnh_numero"
                          value={formData.cnh_numero}
                          onChange={(e) => handleInputChange('cnh_numero', e.target.value)}
                          placeholder="12345678901"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cnh_categoria">Categoria</Label>
                        <Select
                          value={formData.cnh_categoria}
                          onValueChange={(value) => handleInputChange('cnh_categoria', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="A">A</SelectItem>
                            <SelectItem value="B">B</SelectItem>
                            <SelectItem value="C">C</SelectItem>
                            <SelectItem value="D">D</SelectItem>
                            <SelectItem value="E">E</SelectItem>
                            <SelectItem value="AB">AB</SelectItem>
                            <SelectItem value="AC">AC</SelectItem>
                            <SelectItem value="AD">AD</SelectItem>
                            <SelectItem value="AE">AE</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cnh_validade">Validade</Label>
                        <Input
                          id="cnh_validade"
                          type="date"
                          value={formData.cnh_validade}
                          onChange={(e) => handleInputChange('cnh_validade', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  {/* Deficiência Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Deficiência</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="tem_deficiencia"
                            checked={formData.tem_deficiencia}
                            onCheckedChange={(checked) => handleInputChange('tem_deficiencia', checked)}
                          />
                          <Label htmlFor="tem_deficiencia">Possui deficiência</Label>
                        </div>
                      </div>
                      {formData.tem_deficiencia && (
                        <div className="space-y-2">
                          <Label htmlFor="qual_deficiencia">Qual deficiência?</Label>
                          <Input
                            id="qual_deficiencia"
                            value={formData.qual_deficiencia}
                            onChange={(e) => handleInputChange('qual_deficiencia', e.target.value)}
                            placeholder="Descreva a deficiência"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contato de Emergência Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Contato de Emergência</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="contato_emergencia_nome">Nome do Contato</Label>
                        <Input
                          id="contato_emergencia_nome"
                          value={formData.contato_emergencia_nome}
                          onChange={(e) => handleInputChange('contato_emergencia_nome', e.target.value.replace(/\d/g, ''))}
                          placeholder="Nome completo do contato de emergência"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contato_emergencia_telefone">Telefone do Contato</Label>
                        <Input
                          id="contato_emergencia_telefone"
                          value={formData.contato_emergencia_telefone}
                          onChange={(e) => handleInputChange('contato_emergencia_telefone', formatPhone(e.target.value))}
                          placeholder="(11) 99999-9999"
                          maxLength={16}
                        />
                      </div>
                    </div>
                  </div>

                </CardContent>
              </Card>
            </TabsContent>

            {/* Contractual Data Tab */}
            <TabsContent value="contractual">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Building2 className="h-5 w-5" />
                    <span>Informações Contratuais</span>
                  </CardTitle>
                  <CardDescription>
                    Dados do contrato de trabalho
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="empresa">Empresa</Label>
                      <div onPointerDownCapture={() => ensureCompaniesLoaded()}>
                        <SearchableSelect
                          value={formData.company_id || ''}
                          onValueChange={(value) => handleInputChange('company_id', value)}
                          placeholder="Selecione a empresa"
                          items={(options.companies || []).map((company) => ({
                            value: String(company.id),
                            label: company.name
                          }))}
                          emptyMessage={resourceLoading.companies ? 'Carregando empresas...' : 'Nenhuma empresa encontrada'}
                        />
                      </div>
                      {resourceLoading.companies && (
                        <p className="text-xs text-blue-600">Carregando empresas sob demanda...</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="filiais">Filial</Label>
                      <Select
                        value={formData.filiais}
                        onValueChange={(value) => handleInputChange('filiais', value)}
                        onOpenChange={(open) => {
                          if (open) ensureBranchesLoaded();
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a filial" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.isArray(options.branches) && options.branches.map((branch) => (
                            <SelectItem key={branch.id} value={branch.name}>
                              {branch.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="cargo">Cargo</Label>
                      <div className="flex space-x-2">
                        <Select
                          value={formData.cargo}
                          onValueChange={(value) => {
                            handleInputChange('cargo', value);
                            // Auto-lookup salary when both cargo and faixa are selected
                            if (formData.faixa) {
                              handleSalaryLookup(value, formData.faixa);
                            }
                          }}
                          onOpenChange={(open) => {
                            if (open) ensurePositionSalaryCargosLoaded();
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o cargo" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.isArray(options.positionSalaryCargos) && options.positionSalaryCargos.map((position) => (
                              <SelectItem key={position.id} value={position.name}>
                                {position.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="faixa">Faixa</Label>
                      <Select
                        value={formData.faixa}
                        onValueChange={(value) => {
                          handleInputChange('faixa', value);
                          // Auto-lookup salary when both cargo and faixa are selected
                          if (formData.cargo) {
                            handleSalaryLookup(formData.cargo, value);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a faixa" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Júnior I">Júnior I</SelectItem>
                          <SelectItem value="Júnior II">Júnior II</SelectItem>
                          <SelectItem value="Júnior III">Júnior III</SelectItem>
                          <SelectItem value="Pleno I">Pleno I</SelectItem>
                          <SelectItem value="Pleno II">Pleno II</SelectItem>
                          <SelectItem value="Pleno III">Pleno III</SelectItem>
                          <SelectItem value="Sênior I">Sênior I</SelectItem>
                          <SelectItem value="Sênior II">Sênior II</SelectItem>
                          <SelectItem value="Sênior III">Sênior III</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="regime">Regime</Label>
                      <Select
                        value={formData.regime}
                        onValueChange={(value) => handleInputChange('regime', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o regime" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CLT">CLT</SelectItem>
                          <SelectItem value="PJ">PJ</SelectItem>
                          <SelectItem value="ESTAGIARIO">Estagiário</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.regime === 'PJ' && (
                        <div className="flex items-center space-x-2 pt-2">
                          <Checkbox
                            id="possui_decimo_terceiro"
                            checked={formData.possui_decimo_terceiro}
                            onCheckedChange={(checked) => handleInputChange('possui_decimo_terceiro', checked)}
                          />
                          <Label htmlFor="possui_decimo_terceiro" className="text-sm font-normal">Possui Décimo Terceiro</Label>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="adiantamento">
                        Adiantamento Salarial
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <Select
                        value={formData.adiantamento}
                        onValueChange={(value) => handleInputChange('adiantamento', value)}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Opta por adiantamento?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Sim">Sim</SelectItem>
                          <SelectItem value="Não">Não</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.adiantamento === 'Sim' && (() => {
                        const sa = salaryAdvances.find(s => s.company_id === formData.company_id);
                        if (!sa) {
                          return (
                            <p className="text-xs text-amber-600 mt-1">
                              Nenhuma configuração de adiantamento encontrada para esta empresa
                            </p>
                          );
                        }
                        const pct = formData.regime === 'CLT' ? sa.percentage_clt : sa.percentage_pj;
                        if (pct == null) {
                          return (
                            <p className="text-xs text-amber-600 mt-1">
                              Percentual de adiantamento não configurado para regime {formData.regime}
                            </p>
                          );
                        }
                        const valor = formData.salario * pct / 100;
                        return (
                          <p className="text-xs text-green-700 mt-1 font-medium">
                            Valor do adiantamento: R$ {valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} ({pct}%)
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="salario">Salário Base</Label>
                      <CurrencyInput id="salario" value={formData.salario} onChange={v => handleInputChange('salario', v)} placeholder="0,00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cbo">CBO</Label>
                      <Input
                        id="cbo"
                        value={formData.cbo}
                        onChange={(e) => handleInputChange('cbo', e.target.value)}
                        placeholder="Código CBO"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="tipo_remuneracao">Tipo de Remuneração</Label>
                      <Select
                        value={formData.tipo_remuneracao}
                        onValueChange={(value) => handleInputChange('tipo_remuneracao', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mensal">Mensal</SelectItem>
                          <SelectItem value="horista">Horista</SelectItem>
                          <SelectItem value="comissao">Comissão</SelectItem>
                          <SelectItem value="misto">Misto</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tipo_contrato">Tipo de Contrato</Label>
                      <Select
                        value={formData.tipo_contrato}
                        onValueChange={(value) => handleInputChange('tipo_contrato', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="presencial">Presencial</SelectItem>
                          <SelectItem value="hibrido">Híbrido</SelectItem>
                          <SelectItem value="home_office">Home Office</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="admissao">Data de Admissão</Label>
                      <Input
                        id="admissao"
                        type="date"
                        value={formData.admissao}
                        onChange={(e) => handleInputChange('admissao', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ferias_quitadas_ate">
                        Férias Quitadas Até
                        <span className="text-xs text-gray-500 ml-2">(Opcional - para empresas antigas)</span>
                      </Label>
                      <Input
                        id="ferias_quitadas_ate"
                        type="date"
                        value={formData.ferias_quitadas_ate}
                        onChange={(e) => handleInputChange('ferias_quitadas_ate', e.target.value)}
                        placeholder="Informe até quando as férias estão quitadas"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        💡 Use este campo para informar até quando as férias já foram gozadas/pagas.
                        O sistema calculará apenas períodos após esta data.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="departamento">Departamentos</Label>
                      <div
                        className="border rounded-md p-3 max-h-48 overflow-y-auto bg-white"
                        onClick={() => {
                          if (!loadedResources.departments && !resourceLoading.departments) {
                            ensureDepartmentsLoaded();
                          }
                        }}
                      >
                        {resourceLoading.departments ? (
                          <div className="flex items-center justify-center py-4 text-sm text-blue-600">
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Carregando departamentos...
                          </div>
                        ) : Array.isArray(options.departments) && options.departments.length > 0 ? (
                          <div className="space-y-2">
                            {options.departments.map((dept) => {
                              // Converter departamento antigo para array se necessário
                              const selectedDepts = formData.departamentos?.length > 0
                                ? formData.departamentos
                                : (formData.departamento ? [formData.departamento] : []);
                              const isChecked = selectedDepts.includes(dept.name);

                              return (
                                <div key={dept.id} className="flex items-center space-x-2 hover:bg-gray-50 p-1 rounded">
                                  <Checkbox
                                    id={`dept-${dept.id}`}
                                    checked={isChecked}
                                    onCheckedChange={(checked) => {
                                      let newDepts;
                                      if (checked) {
                                        newDepts = [...selectedDepts, dept.name];
                                      } else {
                                        newDepts = selectedDepts.filter(d => d !== dept.name);
                                      }
                                      // Atualizar ambos os campos para compatibilidade
                                      handleInputChange('departamentos', newDepts);
                                      handleInputChange('departamento', newDepts[0] || ''); // Principal = primeiro selecionado
                                    }}
                                  />
                                  <Label
                                    htmlFor={`dept-${dept.id}`}
                                    className="text-sm font-normal cursor-pointer flex-1"
                                  >
                                    {dept.name}
                                  </Label>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                            onClick={(event) => {
                              event.stopPropagation();
                              ensureDepartmentsLoaded();
                            }}
                          >
                            <Search className="h-4 w-4 mr-2" />
                            Carregar departamentos
                          </button>
                        )}
                      </div>
                      {/* Mostrar departamentos selecionados */}
                      {(formData.departamentos?.length > 0 || formData.departamento) && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(formData.departamentos?.length > 0 ? formData.departamentos : [formData.departamento].filter(Boolean)).map((dept, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                            >
                              {dept}
                              <button
                                type="button"
                                className="ml-1 hover:text-blue-600"
                                onClick={() => {
                                  const newDepts = (formData.departamentos || [formData.departamento]).filter(d => d !== dept);
                                  handleInputChange('departamentos', newDepts);
                                  handleInputChange('departamento', newDepts[0] || '');
                                }}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="situacao">Situação</Label>
                      <Select
                        value={formData.situacao}
                        onValueChange={(value) => handleInputChange('situacao', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a situação" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.isArray(options.status) && options.status.map((status) => (
                            <SelectItem key={status.id} value={status.name}>
                              {status.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Campos de Demissão - só aparecem se situação = INATIVO */}
                    {formData.situacao === 'INATIVO' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="data_demissao">Data de Demissão</Label>
                          <Input
                            id="data_demissao"
                            type="date"
                            value={formData.data_demissao}
                            onChange={(e) => handleInputChange('data_demissao', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="tipo_demissao_id">Tipo de Demissão</Label>
                          <Select
                            value={formData.tipo_demissao_id}
                            onValueChange={(value) => handleInputChange('tipo_demissao_id', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o tipo" />
                            </SelectTrigger>
                            <SelectContent>
                              {tiposDemissao.map((tipo) => (
                                <SelectItem key={tipo.id} value={tipo.id}>
                                  {tipo.nome}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Additional Benefits */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Adicionais</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {[
                        { value: 'periculosidade', label: 'Periculosidade' },
                        { value: 'insalubridade', label: 'Insalubridade' },
                        { value: 'gratificacao', label: 'Gratificação' }
                      ].map((adicional) => (
                        <div key={adicional.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={adicional.value}
                            checked={formData.adicionais && formData.adicionais.includes(adicional.value)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFormData(prev => ({
                                  ...prev,
                                  adicionais: [...(prev.adicionais || []), adicional.value]
                                }));
                              } else {
                                setFormData(prev => ({
                                  ...prev,
                                  adicionais: (prev.adicionais || []).filter(item => item !== adicional.value)
                                }));
                              }
                            }}
                          />
                          <Label htmlFor={adicional.value} className="text-sm">
                            {adicional.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CNPJ - Condicional para regime PJ */}
                  {formData.regime === 'PJ' && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Dados PJ</h3>
                      <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="cnpj">CNPJ</Label>
                          <Input
                            id="cnpj"
                            value={formData.cnpj}
                            onChange={(e) => handleInputChange('cnpj', e.target.value)}
                            placeholder="00.000.000/0000-00"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Período de Experiência */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Período de Experiência</h3>

                    {/* Novo Campo: Dias do 1º Período - só obrigatório para CLT */}
                    {formData.regime === 'CLT' && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="space-y-2">
                          <Label htmlFor="dias_primeiro_periodo" className="font-semibold text-blue-900">
                            Dias do 1º Período <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="dias_primeiro_periodo"
                            type="number"
                            min="10"
                            max="80"
                            value={formData.dias_primeiro_periodo || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleInputChange('dias_primeiro_periodo', value);
                            }}
                            placeholder="Digite entre 10 e 80 dias"
                            className="max-w-xs"
                          />
                          <p className="text-xs text-blue-700">
                            💡 Mínimo 10 dias, máximo 80 dias. Total de experiência: 90 dias (dividido em 2 períodos)
                          </p>
                        </div>
                      </div>
                    )}

                    {formData.regime !== 'CLT' && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-sm text-gray-600">
                          ℹ️ O período de experiência de 90 dias é obrigatório apenas para regime CLT
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="experiencia_1periodo_inicio">1º Período - Início</Label>
                        <Input
                          id="experiencia_1periodo_inicio"
                          type="date"
                          value={formData.experiencia_1periodo_inicio}
                          onChange={(e) => handleInputChange('experiencia_1periodo_inicio', e.target.value)}
                          readOnly
                          className="bg-gray-50"
                        />
                        <p className="text-xs text-gray-500">Calculado automaticamente</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="experiencia_1periodo_fim">1º Período - Fim</Label>
                        <Input
                          id="experiencia_1periodo_fim"
                          type="date"
                          value={formData.experiencia_1periodo_fim}
                          onChange={(e) => handleInputChange('experiencia_1periodo_fim', e.target.value)}
                          readOnly
                          className="bg-gray-50"
                        />
                        <p className="text-xs text-gray-500">Calculado automaticamente</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="experiencia_2periodo_inicio">2º Período - Início</Label>
                        <Input
                          id="experiencia_2periodo_inicio"
                          type="date"
                          value={formData.experiencia_2periodo_inicio}
                          onChange={(e) => handleInputChange('experiencia_2periodo_inicio', e.target.value)}
                          readOnly
                          className="bg-gray-50"
                        />
                        <p className="text-xs text-gray-500">Calculado automaticamente</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="experiencia_2periodo_fim">2º Período - Fim</Label>
                        <Input
                          id="experiencia_2periodo_fim"
                          type="date"
                          value={formData.experiencia_2periodo_fim}
                          onChange={(e) => handleInputChange('experiencia_2periodo_fim', e.target.value)}
                          readOnly
                          className="bg-gray-50"
                        />
                        <p className="text-xs text-gray-500">Calculado automaticamente</p>
                      </div>
                    </div>
                  </div>

                  {/* Dados Contratuais Adicionais */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Informações Adicionais</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="matricula">Matrícula</Label>
                        <Input
                          id="matricula"
                          value={formData.matricula}
                          onChange={(e) => handleInputChange('matricula', e.target.value)}
                          placeholder="Número da matrícula"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="superior_direto_id">Superior Direto</Label>
                        <div onPointerDownCapture={() => ensureFuncionariosOptionsLoaded()}>
                          <SearchableSelect
                            value={formData.superior_direto_id || ''}
                            onValueChange={(value) => handleInputChange('superior_direto_id', value)}
                            placeholder="Selecione o superior"
                            items={options.funcionariosOptions.map((func) => ({
                              value: String(func.id),
                              label: `${func.nome}${func.cargo ? ` - ${func.cargo}` : ''}`
                            }))}
                            emptyMessage={resourceLoading.funcionariosOptions ? 'Carregando colaboradores...' : 'Nenhum colaborador encontrado'}
                          />
                        </div>
                        {resourceLoading.funcionariosOptions && (
                          <p className="text-xs text-blue-600">Carregando colaboradores ativos...</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="grau_hierarquia_id">Grau de Hierarquia</Label>
                        <Select
                          value={formData.grau_hierarquia_id}
                          onValueChange={(value) => handleInputChange('grau_hierarquia_id', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o grau" />
                          </SelectTrigger>
                          <SelectContent>
                            {grausHierarquia.map((grau) => (
                              <SelectItem key={grau.id} value={grau.id}>
                                Nível {grau.nivel} - {grau.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            {/* Banking Data Tab */}
            <TabsContent value="banking">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <CreditCard className="h-5 w-5" />
                    <span>Dados Bancários</span>
                  </CardTitle>
                  <CardDescription>
                    Informações para pagamento
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="banco">Banco</Label>
                      <Input
                        id="banco"
                        value={formData.banco}
                        onChange={(e) => handleInputChange('banco', e.target.value)}
                        placeholder="Nome do banco"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="agencia">Agência</Label>
                      <Input
                        id="agencia"
                        value={formData.agencia}
                        onChange={(e) => handleInputChange('agencia', e.target.value)}
                        placeholder="0000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="conta">Conta</Label>
                      <Input
                        id="conta"
                        value={formData.conta}
                        onChange={(e) => handleInputChange('conta', e.target.value)}
                        placeholder="00000-0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tipo_conta">Tipo de Conta</Label>
                      <Select
                        value={formData.tipo_conta}
                        onValueChange={(value) => handleInputChange('tipo_conta', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="corrente">Corrente</SelectItem>
                          <SelectItem value="poupanca">Poupança</SelectItem>
                          <SelectItem value="salario">Salário</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pix">Chave PIX</Label>
                    <Input
                      id="pix"
                      value={formData.pix}
                      onChange={(e) => handleInputChange('pix', e.target.value)}
                      placeholder="CPF, e-mail, telefone ou chave aleatória"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            {/* Dependents Tab */}
            <TabsContent value="dependents">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Users className="h-5 w-5" />
                      <span>Dependentes</span>
                    </div>
                    <Button type="button" onClick={addDependent} variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Dependente
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(!Array.isArray(formData.dependentes) || formData.dependentes.length === 0) ? (
                    <p className="text-slate-500 text-center py-8">
                      Nenhum dependente cadastrado. Clique em "Adicionar Dependente" para incluir.
                    </p>
                  ) : (
                    <div className="space-y-6">
                      {formData.dependentes.map((dependente, index) => (
                        <div key={index} className="border rounded-lg p-4 space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-slate-700">
                              Dependente {index + 1}
                            </h4>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeDependent(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Nome</Label>
                              <Input
                                value={dependente.nome || ''}
                                onChange={(e) => updateDependent(index, 'nome', e.target.value)}
                                placeholder="Nome do dependente"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>CPF</Label>
                              <Input
                                value={dependente.cpf || ''}
                                onChange={(e) => updateDependent(index, 'cpf', formatCPF(e.target.value))}
                                placeholder="000.000.000-00"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Data de Nascimento</Label>
                              <Input
                                type="date"
                                value={dependente.data_nascimento || ''}
                                onChange={(e) => updateDependent(index, 'data_nascimento', e.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Parentesco</Label>
                              <Select
                                value={dependente.parentesco || ''}
                                onValueChange={(value) => updateDependent(index, 'parentesco', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="filho">Filho(a)</SelectItem>
                                  <SelectItem value="conjuge">Cônjuge</SelectItem>
                                  <SelectItem value="pai">Pai</SelectItem>
                                  <SelectItem value="mae">Mãe</SelectItem>
                                  <SelectItem value="outro">Outro</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`imposto_renda_${index}`}
                                checked={dependente.imposto_renda || false}
                                onCheckedChange={(checked) => updateDependent(index, 'imposto_renda', checked)}
                              />
                              <Label htmlFor={`imposto_renda_${index}`}>
                                Dependente para Imposto de Renda
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`atipico_${index}`}
                                checked={dependente.atipico || false}
                                onCheckedChange={(checked) => updateDependent(index, 'atipico', checked)}
                              />
                              <Label htmlFor={`atipico_${index}`}>
                                Dependente Atípico
                              </Label>
                            </div>
                          </div>

                          <div className="border-t pt-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <h5 className="font-medium text-slate-700">Benefícios do dependente</h5>
                                <p className="text-xs text-slate-500">O valor é individual para cada dependente.</p>
                              </div>
                              <Button type="button" variant="outline" size="sm" onClick={() => addDependentBenefit(index)}>
                                <Plus className="h-4 w-4 mr-1" /> Adicionar benefício
                              </Button>
                            </div>
                            {(dependente.beneficios || []).length === 0 ? (
                              <p className="text-xs text-slate-500">Nenhum benefício atribuído a este dependente.</p>
                            ) : (
                              <div className="space-y-3">
                                {(dependente.beneficios || []).map((beneficio, beneficioIndex) => (
                                  <div key={beneficioIndex} className="rounded-md border bg-slate-50 p-3 space-y-3">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-semibold text-slate-600">Benefício {beneficioIndex + 1}</span>
                                      <Button type="button" variant="ghost" size="sm" className="text-red-600"
                                        onClick={() => removeDependentBenefit(index, beneficioIndex)}>
                                        <Minus className="h-4 w-4" />
                                      </Button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                      <div className="space-y-1">
                                        <Label>Tipo</Label>
                                        <Select value={beneficio.tipo || 'outro'} onValueChange={(value) => updateDependentBenefit(index, beneficioIndex, 'tipo', value)}>
                                          <SelectTrigger><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            {BENEFICIOS_DEPENDENTE.map(opcao => <SelectItem key={opcao.value} value={opcao.value}>{opcao.label}</SelectItem>)}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div className="space-y-1">
                                        <Label>Descrição</Label>
                                        <Input value={beneficio.descricao || ''} onChange={(e) => updateDependentBenefit(index, beneficioIndex, 'descricao', e.target.value)}
                                          placeholder="Ex.: Plano Ouro" />
                                      </div>
                                      <div className="space-y-1">
                                        <Label>Valor mensal (R$) *</Label>
                                        <Input type="text" inputMode="decimal" value={beneficio.valor || ''}
                                          placeholder="R$ 0,00"
                                          onChange={(e) => updateDependentBenefit(index, beneficioIndex, 'valor', e.target.value)} />
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Checkbox id={`dep_benef_ativo_${index}_${beneficioIndex}`} checked={beneficio.ativo !== false}
                                        onCheckedChange={(checked) => updateDependentBenefit(index, beneficioIndex, 'ativo', checked)} />
                                      <Label htmlFor={`dep_benef_ativo_${index}_${beneficioIndex}`}>Benefício ativo</Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Checkbox id={`dep_benef_deduzir_${index}_${beneficioIndex}`} checked={beneficio.deduzir_folha === true}
                                        onCheckedChange={(checked) => updateDependentBenefit(index, beneficioIndex, 'deduzir_folha', checked)} />
                                      <Label htmlFor={`dep_benef_deduzir_${index}_${beneficioIndex}`}>Deduzir em folha de pagamento</Label>
                                    </div>
                                    {beneficio.deduzir_folha === true && (
                                      <div className="space-y-2">
                                        <Label className="text-xs">Aplicar dedução em</Label>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                          {COMPETENCIAS_BENEFICIO_DEPENDENTE.map(opcao => {
                                            const selecionadas = beneficio.competencias || [];
                                            return (
                                              <label key={opcao.value} className="flex items-center gap-2 text-xs cursor-pointer">
                                                <Checkbox checked={selecionadas.includes(opcao.value)} onCheckedChange={(checked) => {
                                                  const novas = checked
                                                    ? [...new Set([...selecionadas, opcao.value])]
                                                    : selecionadas.filter(v => v !== opcao.value);
                                                  updateDependentBenefit(index, beneficioIndex, 'competencias', novas);
                                                }} />
                                                {opcao.label}
                                              </label>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            {/* Documents Tab */}
            <TabsContent value="documents">
              <DocumentosFuncionarioSection funcionarioId={formData.id} funcionarioNome={formData.nome} />
            </TabsContent>
            {/* Benefits Tab */}
            <TabsContent value="benefits">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Gift className="h-5 w-5" />
                    <span>Benefícios</span>
                  </CardTitle>
                  <CardDescription>
                    Configure os benefícios do funcionário
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Health Benefits */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Saúde</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex items-start space-x-4">
                        <Checkbox
                          id="plano_saude_enabled"
                          checked={benefits.plano_saude_enabled}
                          onCheckedChange={(checked) => {
                            setBenefits(prev => ({ ...prev, plano_saude_enabled: checked }));
                            if (!checked) {
                              handleInputChange('assist_medica', 0);
                            }
                          }}
                        />
                        <div className="flex-1">
                          <Label htmlFor="plano_saude_enabled" className="text-base font-medium">
                            Plano de Saúde
                          </Label>
                          {benefits.plano_saude_enabled && (
                            <div className="mt-2">
                              <CurrencyInput value={formData.assist_medica} onChange={v => handleInputChange('assist_medica', v)} placeholder="0,00" />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-start space-x-4">
                        <Checkbox
                          id="odonto_enabled"
                          checked={benefits.odonto_enabled}
                          onCheckedChange={(checked) => {
                            setBenefits(prev => ({ ...prev, odonto_enabled: checked }));
                            if (!checked) {
                              handleInputChange('odonto', 'Nenhuma');
                            }
                          }}
                        />
                        <div className="flex-1">
                          <Label htmlFor="odonto_enabled" className="text-base font-medium">
                            Plano Odontológico
                          </Label>
                          {benefits.odonto_enabled && (
                            <div className="mt-2">
                              <Select
                                value={formData.odonto}
                                onValueChange={(value) => handleInputChange('odonto', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione uma opção" />
                                </SelectTrigger>
                                <SelectContent>
                                  {Array.isArray(options.odontoOptions) && options.odontoOptions.map((option) => (
                                    <SelectItem key={option.id} value={option.name}>
                                      {option.name} - {formatCurrency(option.value)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Insurance Benefits */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Seguros</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex items-start space-x-4">
                        <Checkbox
                          id="seg_vida_enabled"
                          checked={benefits.seg_vida_enabled}
                          onCheckedChange={(checked) => {
                            setBenefits(prev => ({ ...prev, seg_vida_enabled: checked }));
                            if (!checked) {
                              handleInputChange('seg_vida', 'Nenhuma');
                            }
                          }}
                        />
                        <div className="flex-1">
                          <Label htmlFor="seg_vida_enabled" className="text-base font-medium">
                            Seguro de Vida
                          </Label>
                          {benefits.seg_vida_enabled && (
                            <div className="mt-2">
                              <Select
                                value={formData.seg_vida}
                                onValueChange={(value) => handleInputChange('seg_vida', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione uma opção" />
                                </SelectTrigger>
                                <SelectContent>
                                  {Array.isArray(options.segVidaOptions) && options.segVidaOptions.map((option) => (
                                    <SelectItem key={option.id} value={option.name}>
                                      {option.name} - {formatCurrency(option.value)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-start space-x-4">
                        <Checkbox
                          id="total_pass_enabled"
                          checked={benefits.total_pass_enabled}
                          onCheckedChange={(checked) => {
                            setBenefits(prev => ({ ...prev, total_pass_enabled: checked }));
                            if (!checked) {
                              handleInputChange('total_pass', 'Nenhuma');
                            }
                          }}
                        />
                        <div className="flex-1">
                          <Label htmlFor="total_pass_enabled" className="text-base font-medium">
                            Total Pass
                          </Label>
                          {benefits.total_pass_enabled && (
                            <div className="mt-2">
                              <Select
                                value={formData.total_pass}
                                onValueChange={(value) => handleInputChange('total_pass', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione uma opção" />
                                </SelectTrigger>
                                <SelectContent>
                                  {Array.isArray(options.totalPassOptions) && options.totalPassOptions.map((option) => (
                                    <SelectItem key={option.id} value={option.name}>
                                      {option.name} - {formatCurrency(option.value)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Occupational Medicine */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Medicina Ocupacional</h3>

                    <div className="flex items-start space-x-4">
                      <Checkbox
                        id="med_ocupacional_enabled"
                        checked={benefits.med_ocupacional_enabled}
                        onCheckedChange={(checked) => {
                          setBenefits(prev => ({ ...prev, med_ocupacional_enabled: checked }));
                          if (!checked) {
                            handleInputChange('med_ocupacional', 'Nenhuma');
                          }
                        }}
                      />
                      <div className="flex-1">
                        <Label htmlFor="med_ocupacional_enabled" className="text-base font-medium">
                          Medicina Ocupacional
                        </Label>
                        {benefits.med_ocupacional_enabled && (
                          <div className="mt-2">
                            <Select
                              value={formData.med_ocupacional}
                              onValueChange={(value) => handleInputChange('med_ocupacional', value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione uma opção" />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.isArray(options.medOcupacionalOptions) && options.medOcupacionalOptions.map((option) => (
                                  <SelectItem key={option.id} value={option.name}>
                                    {option.name} - {formatCurrency(option.value)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Other Benefits */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Outros Benefícios</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="auxilio_creche">Auxílio Creche</Label>
                        <CurrencyInput id="auxilio_creche" value={formData.auxilio_creche} onChange={v => handleInputChange('auxilio_creche', v)} placeholder="0,00" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="aux_combustivel">Auxílio Combustível</Label>
                        <CurrencyInput id="aux_combustivel" value={formData.aux_combustivel} onChange={v => handleInputChange('aux_combustivel', v)} placeholder="0,00" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="segunda_refeicao">Segunda Refeição</Label>
                        <CurrencyInput id="segunda_refeicao" value={formData.segunda_refeicao} onChange={v => handleInputChange('segunda_refeicao', v)} placeholder="0,00" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pernoite">Pernoite</Label>
                        <CurrencyInput id="pernoite" value={formData.pernoite} onChange={v => handleInputChange('pernoite', v)} placeholder="0,00" />
                      </div>
                    </div>

                    {/* Benefícios extras dinâmicos */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-base font-medium">Benefícios Extras</Label>
                        <Button type="button" variant="outline" size="sm" onClick={addOutroBeneficio}>
                          <Plus className="h-4 w-4 mr-1" /> Adicionar
                        </Button>
                      </div>
                      {Array.isArray(formData.beneficios_outros) && formData.beneficios_outros.map((item, index) => (
                        <div key={index} className="flex items-start gap-2 p-3 border rounded-lg bg-slate-50">
                          <div className="flex-1 space-y-1">
                            <Input
                              value={item.descricao}
                              onChange={(e) => updateOutroBeneficio(index, 'descricao', e.target.value)}
                              placeholder="Descrição do benefício"
                            />
                          </div>
                          <div className="w-40 space-y-1">
                            <CurrencyInput
                              value={item.valor}
                              onChange={(v) => updateOutroBeneficio(index, 'valor', v)}
                              placeholder="0,00"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeOutroBeneficio(index)}
                            className="text-red-500 hover:text-red-700 mt-1"
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      {(!formData.beneficios_outros || formData.beneficios_outros.length === 0) && (
                        <p className="text-sm text-slate-500 italic">
                          Nenhum benefício extra cadastrado. Clique em "Adicionar" para incluir.
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            {/* Calculations Tab (Unified from EmployeeForm) */}
            <TabsContent value="calculations">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Calculator className="h-5 w-5" />
                    <span>Cálculos e Valores</span>
                  </CardTitle>
                  <CardDescription>
                    Informações financeiras e cálculos automáticos
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> */}
                  {/* <div className="space-y-2">
                      <Label htmlFor="calc_empresa">Empresa</Label>
                      <Select
                        value={formData.company_id}
                        onValueChange={(value) => handleInputChange('company_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a empresa" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.isArray(options.companies) && options.companies.map((company) => (
                            <SelectItem key={company.id} value={company.id}>
                              {company.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div> */}
                  {/* <div className="space-y-2">
                      <Label htmlFor="calc_filiais">Filial</Label>
                      <Select
                        value={formData.filiais}
                        onValueChange={(value) => handleInputChange('filiais', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a filial" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.isArray(options.branches) && options.branches.map((branch) => (
                            <SelectItem key={branch.id} value={branch.name}>
                              {branch.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div> */}
                  {/* </div> */}

                  {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="calc_cargo">Cargo</Label>
                      <Select
                        value={formData.cargo}
                        onValueChange={(value) => {
                          handleInputChange('cargo', value);
                          // Auto-lookup salary when both cargo and faixa are selected
                          if (formData.faixa) {
                            handleSalaryLookup(value, formData.faixa);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o cargo" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.isArray(options.positionSalaryCargos) && options.positionSalaryCargos.map((position) => (
                            <SelectItem key={position.id} value={position.name}>
                              {position.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="calc_faixa">Faixa</Label>
                      <Select
                        value={formData.faixa}
                        onValueChange={(value) => {
                          handleInputChange('faixa', value);
                          // Auto-lookup salary when both cargo and faixa are selected
                          if (formData.cargo) {
                            handleSalaryLookup(formData.cargo, value);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a faixa" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Júnior I">Júnior I</SelectItem>
                          <SelectItem value="Júnior II">Júnior II</SelectItem>
                          <SelectItem value="Júnior III">Júnior III</SelectItem>
                          <SelectItem value="Pleno I">Pleno I</SelectItem>
                          <SelectItem value="Pleno II">Pleno II</SelectItem>
                          <SelectItem value="Pleno III">Pleno III</SelectItem>
                          <SelectItem value="Sênior I">Sênior I</SelectItem>
                          <SelectItem value="Sênior II">Sênior II</SelectItem>
                          <SelectItem value="Sênior III">Sênior III</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div> */}
                  {/* 
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="calc_departamento">Departamentos</Label>
                      <div className="border rounded-md p-3 max-h-48 overflow-y-auto bg-white">
                        {Array.isArray(options.departments) && options.departments.length > 0 ? (
                          <div className="space-y-2">
                            {options.departments.map((dept) => {
                              const selectedDepts = formData.departamentos?.length > 0
                                ? formData.departamentos
                                : (formData.departamento ? [formData.departamento] : []);
                              const isChecked = selectedDepts.includes(dept.name);

                              return (
                                <div key={dept.id} className="flex items-center space-x-2 hover:bg-gray-50 p-1 rounded">
                                  <Checkbox
                                    id={`calc-dept-${dept.id}`}
                                    checked={isChecked}
                                    onCheckedChange={(checked) => {
                                      let newDepts;
                                      if (checked) {
                                        newDepts = [...selectedDepts, dept.name];
                                      } else {
                                        newDepts = selectedDepts.filter(d => d !== dept.name);
                                      }
                                      handleInputChange('departamentos', newDepts);
                                      handleInputChange('departamento', newDepts[0] || '');
                                    }}
                                  />
                                  <Label
                                    htmlFor={`calc-dept-${dept.id}`}
                                    className="text-sm font-normal cursor-pointer flex-1"
                                  >
                                    {dept.name}
                                  </Label>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">Nenhum departamento disponível</p>
                        )}
                      </div>
                      {(formData.departamentos?.length > 0 || formData.departamento) && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(formData.departamentos?.length > 0 ? formData.departamentos : [formData.departamento].filter(Boolean)).map((dept, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                            >
                              {dept}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div> */}

                  {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="calc_situacao">Situação</Label>
                      <Select
                        value={formData.situacao}
                        onValueChange={(value) => handleInputChange('situacao', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a situação" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.isArray(options.status) && options.status.map((status) => (
                            <SelectItem key={status.id} value={status.name}>
                              {status.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="calc_data_demissao">Data de Demissão</Label>
                      <Input
                        id="calc_data_demissao"
                        type="date"
                        value={formData.data_demissao}
                        onChange={(e) => handleInputChange('data_demissao', e.target.value)}
                      />
                    </div>
                  </div> */}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* <div className="space-y-2"> */}
                    {/* <Label htmlFor="calc_regime">Regime</Label>
                      <Select
                        value={formData.regime}
                        onValueChange={(value) => handleInputChange('regime', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o regime" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CLT">CLT</SelectItem>
                          <SelectItem value="PJ">PJ</SelectItem>
                        </SelectContent>
                      </Select> */}
                    {/* </div> */}
                    <div className="space-y-2">
                      <Label>Salário</Label>
                      <p>
                        {Number(formData.salario).toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Financial Fields */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Campos Financeiros</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="calc_acumulo_funcao">Acúmulo de Função</Label>
                        <CurrencyInput id="calc_acumulo_funcao" value={formData.acumulo_funcao} onChange={v => handleInputChange('acumulo_funcao', v)} placeholder="0,00" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="calc_hora_extra">Hora Extra</Label>
                        <CurrencyInput id="calc_hora_extra" value={formData.hora_extra} onChange={v => handleInputChange('hora_extra', v)} placeholder="0,00" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="calc_comissao">Comissão</Label>
                        <CurrencyInput id="calc_comissao" value={formData.comissao} onChange={v => handleInputChange('comissao', v)} placeholder="0,00" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="calc_ajuda_custo">Ajuda de Custo</Label>
                        <CurrencyInput id="calc_ajuda_custo" value={formData.ajuda_custo} onChange={v => handleInputChange('ajuda_custo', v)} placeholder="0,00" />
                      </div>
                    </div>
                  </div>

                  {/* Transport and Food */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Transporte e Alimentação</h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="calc_vt">VT (dia)</Label>
                        <CurrencyInput id="calc_vt" value={formData.vt} onChange={v => handleInputChange('vt', v)} placeholder="0,00" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="calc_desconto_vt">Desconto VT</Label>
                        <CurrencyInput id="calc_desconto_vt" value={formData.desconto_vt} disabled className="bg-slate-50" placeholder="0,00" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="calc_vt_custo">VT Custo</Label>
                        <CurrencyInput id="calc_vt_custo" value={formData.vt_custo} onChange={v => handleInputChange('vt_custo', v)} placeholder="0,00" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="calc_taxa_vt">Taxa VT</Label>
                        <CurrencyInput id="calc_taxa_vt" value={formData.taxa_vt} onChange={v => handleInputChange('taxa_vt', v)} placeholder="0,00" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="calc_repasse_vt">Repasse VT</Label>
                        <CurrencyInput id="calc_repasse_vt" value={formData.repasse_vt} onChange={v => handleInputChange('repasse_vt', v)} placeholder="0,00" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Transporte</Label>
                        <Select
                          value={formData.transporte_id}
                          onValueChange={(value) => handleInputChange('transporte_id', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione um transporte" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Nenhum">Nenhum</SelectItem>
                            {Array.isArray(options.transporteOptions) && options.transporteOptions.map((option) => (
                              <SelectItem key={option.id} value={option.id}>
                                {option.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="calc_va">VA (dia)</Label>
                        <CurrencyInput id="calc_va" value={formData.va} onChange={v => handleInputChange('va', v)} placeholder="0,00" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="calc_vr">VR (dia)</Label>
                        <CurrencyInput id="calc_vr" value={formData.vr} onChange={v => handleInputChange('vr', v)} placeholder="0,00" />
                      </div>
                    </div>
                  </div>

                  {/* Health and Selection Options */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Opções de Seleção</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="calc_odonto">Odonto</Label>
                        <Select
                          value={formData.odonto}
                          onValueChange={(value) => handleInputChange('odonto', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione uma opção" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Nenhuma">Nenhuma</SelectItem>
                            {Array.isArray(options.odontoOptions) && options.odontoOptions.map((option) => (
                              <SelectItem key={option.id} value={option.name}>
                                {option.name} - {formatCurrency(option.value)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="calc_seg_vida">Seg. Vida</Label>
                        <Select
                          value={formData.seg_vida}
                          onValueChange={(value) => handleInputChange('seg_vida', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione uma opção" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Nenhuma">Nenhuma</SelectItem>
                            {Array.isArray(options.segVidaOptions) && options.segVidaOptions.map((option) => (
                              <SelectItem key={option.id} value={option.name}>
                                {option.name} - {formatCurrency(option.value)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="calc_total_pass">Total Pass</Label>
                        <Select
                          value={formData.total_pass}
                          onValueChange={(value) => handleInputChange('total_pass', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione uma opção" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Nenhuma">Nenhuma</SelectItem>
                            {Array.isArray(options.totalPassOptions) && options.totalPassOptions.map((option) => (
                              <SelectItem key={option.id} value={option.name}>
                                {option.name} - {formatCurrency(option.value)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            {/* Beneficiarios Tab */}
            <TabsContent value="beneficiarios">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Heart className="h-5 w-5" />
                      <span>Beneficiários</span>
                    </div>
                    <Button type="button" onClick={addBeneficiario} variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Beneficiário
                    </Button>
                  </CardTitle>
                  <CardDescription>
                    Cadastre os beneficiários e vincule aos dependentes do funcionário
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {(!Array.isArray(formData.beneficiarios) || formData.beneficiarios.length === 0) ? (
                    <p className="text-slate-500 text-center py-8">
                      Nenhum beneficiário cadastrado. Clique em "Adicionar Beneficiário" para incluir.
                    </p>
                  ) : (
                    <div className="space-y-6">
                      {formData.beneficiarios.map((beneficiario, index) => (
                        <div key={index} className="border rounded-lg p-4 space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-slate-700">
                              Beneficiário {index + 1}
                            </h4>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeBeneficiario(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Nome</Label>
                              <Input
                                value={beneficiario.nome || ''}
                                onChange={(e) => updateBeneficiario(index, 'nome', e.target.value)}
                                placeholder="Nome do beneficiário"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>CPF</Label>
                              <Input
                                value={beneficiario.cpf || ''}
                                onChange={(e) => updateBeneficiario(index, 'cpf', formatCPF(e.target.value))}
                                placeholder="000.000.000-00"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>RG</Label>
                              <Input
                                value={beneficiario.rg || ''}
                                onChange={(e) => updateBeneficiario(index, 'rg', e.target.value)}
                                placeholder="RG do beneficiário"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Data de Nascimento</Label>
                              <Input
                                type="date"
                                value={beneficiario.data_nascimento || ''}
                                onChange={(e) => updateBeneficiario(index, 'data_nascimento', e.target.value)}
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label>Tipo de Benefício</Label>
                            <Select
                              value={beneficiario.tipo_beneficio || ''}
                              onValueChange={(value) => updateBeneficiario(index, 'tipo_beneficio', value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione o tipo" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pensao">Pensão</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {beneficiario.tipo_beneficio === 'pensao' && (
                            <div className="space-y-2">
                              <Label>Aplicar Pensão em</Label>
                              <div className="grid grid-cols-2 gap-2">
                                {[
                                  { value: 'folha_mensal', label: 'Folha Mensal' },
                                  { value: 'adiantamento', label: 'Adiantamento' },
                                  { value: '13_primeira', label: '13º Primeira Parcela' },
                                  { value: '13_segunda', label: '13º Segunda Parcela' },
                                  { value: 'ferias', label: 'Férias' },
                                  { value: 'rescisao', label: 'Rescisão' },
                                ].map(({ value, label }) => (
                                  <label key={value} className="flex items-center gap-2 cursor-pointer">
                                    <Checkbox
                                      checked={(beneficiario.tipos_competencia || []).includes(value)}
                                      onCheckedChange={(checked) => {
                                        const current = beneficiario.tipos_competencia || []
                                        const updated = checked
                                          ? [...current, value]
                                          : current.filter(v => v !== value)
                                        updateBeneficiario(index, 'tipos_competencia', updated)
                                      }}
                                    />
                                    <span className="text-xs text-slate-600">{label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="space-y-3">
                            <Label>Dependentes Vinculados</Label>
                            <div className="border rounded-md divide-y">
                              {(formData.dependentes || []).map((dep, depIndex) => {
                                const vinculo = (beneficiario.dependentes_vinculados || []).find(
                                  v => typeof v === 'object' && v.dependente_index === depIndex
                                )
                                const isVinculado = !!vinculo
                                return (
                                  <div key={depIndex} className="p-3">
                                    <div className="flex items-start gap-3">
                                      <Checkbox
                                        checked={isVinculado}
                                        onCheckedChange={(checked) => {
                                          // Filtra apenas objetos (remove formato antigo com inteiros)
                                          const objetos = (beneficiario.dependentes_vinculados || []).filter(
                                            v => typeof v === 'object'
                                          )
                                          const novos = [...objetos]
                                          if (checked) {
                                            novos.push({
                                              dependente_index: depIndex,
                                              tipo_calculo: 'valor_fixo',
                                              valor_pagar: 0
                                            })
                                          } else {
                                            const idx = novos.findIndex(v => v.dependente_index === depIndex)
                                            if (idx >= 0) novos.splice(idx, 1)
                                          }
                                          updateBeneficiario(index, 'dependentes_vinculados', novos)
                                        }}
                                      />
                                      <div className="flex-1 min-w-0">
                                        <Label className="font-medium cursor-pointer">{dep.nome || `Dependente ${depIndex + 1}`}</Label>
                                        {isVinculado && (
                                          <div className="grid grid-cols-2 gap-2 mt-2">
                                            <div>
                                              <Select
                                                value={vinculo.tipo_calculo}
                                                onValueChange={(value) => {
                                                  const novos = [...(beneficiario.dependentes_vinculados || [])]
                                                  const idx = novos.findIndex(
                                                    v => typeof v === 'object' && v.dependente_index === depIndex
                                                  )
                                                  if (idx >= 0) {
                                                    novos[idx] = { ...novos[idx], tipo_calculo: value }
                                                  }
                                                  updateBeneficiario(index, 'dependentes_vinculados', novos)
                                                }}
                                              >
                                                <SelectTrigger className="h-9 text-xs">
                                                  <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="valor_fixo">Valor Fixo (R$)</SelectItem>
                                                  <SelectItem value="salario_minimo">% Salário Mínimo</SelectItem>
                                                  <SelectItem value="salario_base">% Salário Base</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            </div>
                                            <div className="relative">
                                              {vinculo.tipo_calculo === 'valor_fixo' && (
                                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs">R$</span>
                                              )}
                                              {(vinculo.tipo_calculo === 'salario_minimo' || vinculo.tipo_calculo === 'salario_base') && (
                                                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                                              )}
                                              <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max={vinculo.tipo_calculo !== 'valor_fixo' ? 100 : undefined}
                                                value={vinculo.valor_pagar || ''}
                                                onChange={(e) => {
                                                  const novos = [...(beneficiario.dependentes_vinculados || [])]
                                                  const idx = novos.findIndex(
                                                    v => typeof v === 'object' && v.dependente_index === depIndex
                                                  )
                                                  if (idx >= 0) {
                                                    novos[idx] = { ...novos[idx], valor_pagar: parseFloat(e.target.value) || 0 }
                                                  }
                                                  updateBeneficiario(index, 'dependentes_vinculados', novos)
                                                }}
                                                placeholder={vinculo.tipo_calculo === 'valor_fixo' ? '0.00' : '0'}
                                                className={`h-9 text-xs ${vinculo.tipo_calculo === 'valor_fixo' ? 'pl-8' : ''}`}
                                              />
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                            {(!formData.dependentes || formData.dependentes.length === 0) && (
                              <p className="text-xs text-amber-600">
                                Cadastre dependentes primeiro para vinculá-los ao beneficiário
                              </p>
                            )}
                          </div>

                          <div className="border-t pt-4">
                            <div className="space-y-2 mb-4">
                              <Label>Forma de Recebimento</Label>
                              <Select
                                value={beneficiario.forma_recebimento || 'banco'}
                                onValueChange={(value) => updateBeneficiario(index, 'forma_recebimento', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="banco">Informações Bancárias</SelectItem>
                                  <SelectItem value="pix">Pix</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {(beneficiario.forma_recebimento || 'banco') === 'pix' ? (
                              <div className="space-y-2">
                                <Label>Chave PIX</Label>
                                <Input
                                  value={beneficiario.chave_pix || ''}
                                  onChange={(e) => updateBeneficiario(index, 'chave_pix', e.target.value)}
                                  placeholder="CPF, e-mail, telefone ou chave aleatória"
                                />
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                  <Label>Banco</Label>
                                  <Input
                                    value={beneficiario.banco || ''}
                                    onChange={(e) => updateBeneficiario(index, 'banco', e.target.value.replace(/\D/g, ''))}
                                    placeholder="Apenas números"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Agência</Label>
                                  <Input
                                    value={beneficiario.agencia || ''}
                                    onChange={(e) => updateBeneficiario(index, 'agencia', e.target.value.replace(/\D/g, ''))}
                                    placeholder="Apenas números"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Conta</Label>
                                  <Input
                                    value={beneficiario.conta || ''}
                                    onChange={(e) => updateBeneficiario(index, 'conta', e.target.value.replace(/\D/g, ''))}
                                    placeholder="Apenas números"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            {/* Geolocation Tab */}
            <TabsContent value="geolocation">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <MapPin className="h-5 w-5 text-blue-600" />
                    <span>Geolocalização e Transporte</span>
                  </CardTitle>
                  <CardDescription>
                    Informações de localização e opções de transporte público para o funcionário
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">

                  {/* Address Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Informações de Endereço</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Endereço do Funcionário (Origem)</Label>
                        <div className="p-3 bg-slate-50 rounded-md text-sm">
                          <div className="mb-2">
                            <strong>Endereço Completo:</strong>
                            <p className="mt-1 text-blue-600 font-medium">{getCompleteEmployeeAddress()}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                            <p><strong>Logradouro:</strong> {formData.logradouro || 'Não informado'}</p>
                            <p><strong>Número:</strong> {formData.numero || 'S/N'}</p>
                            <p><strong>Bairro:</strong> {formData.bairro || 'Não informado'}</p>
                            <p><strong>Cidade:</strong> {formData.cidade || 'Não informado'}</p>
                            <p><strong>Estado:</strong> {formData.estado || 'Não informado'}</p>
                            <p><strong>CEP:</strong> {formData.cep || 'Não informado'}</p>
                          </div>
                          {!hasValidEmployeeAddress() && (
                            <div className="mt-2 p-2 bg-orange-100 border border-orange-300 rounded text-orange-700 text-xs">
                              ⚠️ Para calcular a rota, preencha pelo menos: Logradouro, Cidade e Estado na aba "Endereço"
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Endereço da Empresa (Destino)</Label>
                        {defaultAddress ? (
                          <div className="p-3 bg-blue-50 rounded-md text-sm">
                            <p><strong>Empresa:</strong> {defaultAddress.nome_empresa}</p>
                            <p><strong>Endereço:</strong> {defaultAddress.endereco_completo}</p>
                            <p><strong>Cidade:</strong> {defaultAddress.cidade}</p>
                            <p><strong>Estado:</strong> {defaultAddress.estado}</p>
                            <p><strong>CEP:</strong> {defaultAddress.cep}</p>
                          </div>
                        ) : (
                          <div className="p-3 bg-orange-50 border border-orange-200 rounded-md text-sm text-orange-700">
                            <AlertCircle className="h-4 w-4 inline mr-2" />
                            Nenhum endereço padrão da empresa configurado.
                            <br />Configure em <strong>Cadastros → Endereços</strong>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <Button
                        type="button"
                        onClick={calculateGeolocation}
                        disabled={geolocationLoading || !hasValidEmployeeAddress() || !defaultAddress}
                        className="btn-primary"
                      >
                        {geolocationLoading ? (
                          <>
                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                            Calculando...
                          </>
                        ) : (
                          <>
                            <Navigation className="h-4 w-4 mr-2" />
                            Calcular Rota
                          </>
                        )}
                      </Button>

                      {geolocationData && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setGeolocationData(null)}
                        >
                          Limpar Dados
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Geolocation Results */}
                  {geolocationData && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-slate-700 border-b pb-2">Resultados da Geolocalização</h3>

                      {/* Summary */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center space-x-2 text-green-700">
                            <Route className="h-5 w-5" />
                            <span className="font-medium">Distância Total</span>
                          </div>
                          <p className="text-2xl font-bold text-green-800">{geolocationData.distance}</p>
                        </div>

                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center space-x-2 text-blue-700">
                            <Clock className="h-5 w-5" />
                            <span className="font-medium">Tempo Total</span>
                          </div>
                          <p className="text-2xl font-bold text-blue-800">{geolocationData.duration}</p>
                        </div>

                        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                          <div className="flex items-center space-x-2 text-purple-700">
                            <MapPin className="h-5 w-5" />
                            <span className="font-medium">Rotas Disponíveis</span>
                          </div>
                          <p className="text-2xl font-bold text-purple-800">{geolocationData.routes?.length || 0}</p>
                        </div>
                      </div>

                      {/* Car Route Information */}
                      {geolocationData.driving_info && (
                        <div className="space-y-4">
                          <h4 className="text-md font-medium text-slate-700 flex items-center space-x-2">
                            <span>🚗</span>
                            <span>Rota de Carro</span>
                          </h4>

                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                                  <Route className="h-5 w-5 text-orange-600" />
                                </div>
                                <div>
                                  <p className="text-sm text-orange-700 font-medium">Distância</p>
                                  <p className="text-lg font-bold text-orange-800">{geolocationData.driving_info.distance}</p>
                                </div>
                              </div>

                              <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                                  <Clock className="h-5 w-5 text-orange-600" />
                                </div>
                                <div>
                                  <p className="text-sm text-orange-700 font-medium">Tempo Estimado</p>
                                  <p className="text-lg font-bold text-orange-800">{geolocationData.driving_info.duration}</p>
                                </div>
                              </div>
                            </div>

                            <div className="mt-3 pt-3 border-t border-orange-200">
                              <div className="flex items-center space-x-2 text-sm text-orange-700">
                                <span>ℹ️</span>
                                <span>Tempo baseado em condições normais de trânsito. Pode variar conforme horário e condições climáticas.</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Public Transport Routes Information */}
                      {geolocationData.routes && geolocationData.routes.length > 0 && (
                        <div className="space-y-4">
                          <h4 className="text-md font-medium text-slate-700 flex items-center space-x-2">
                            <span>🚌</span>
                            <span>Opções de Transporte Público</span>
                          </h4>

                          {geolocationData.routes.map((route, routeIndex) => (
                            <div key={routeIndex} className="border border-slate-200 rounded-lg p-4">
                              <div className="flex justify-between items-center mb-3">
                                <h5 className="font-medium text-slate-800">{route.summary}</h5>
                                <div className="text-sm text-slate-600">
                                  <span className="mr-4">{route.distance}</span>
                                  <span>{route.duration}</span>
                                </div>
                              </div>

                              <div className="space-y-2">
                                {route.transport_modes && route.transport_modes.map((mode, modeIndex) => (
                                  <div key={modeIndex} className="flex items-center space-x-3 p-2 bg-slate-50 rounded">
                                    <div className="flex-shrink-0">
                                      {mode.mode === 'Caminhada' && <span className="text-orange-600">🚶</span>}
                                      {mode.mode === 'Metrô' && <span className="text-blue-600">🚇</span>}
                                      {mode.mode === 'Ônibus' && <span className="text-green-600">🚌</span>}
                                      {mode.mode === 'VLT' && <span className="text-purple-600">🚊</span>}
                                    </div>
                                    <div className="flex-grow">
                                      <div className="flex justify-between items-center">
                                        <span className="font-medium text-sm">{mode.mode}</span>
                                        <div className="text-xs text-slate-600">
                                          <span className="mr-2">{mode.duration}</span>
                                          <span>{mode.distance}</span>
                                        </div>
                                      </div>
                                      <p className="text-xs text-slate-600">{mode.instructions}</p>
                                      {mode.line && (
                                        <p className="text-xs text-blue-600 font-medium">Linha: {mode.line}</p>
                                      )}
                                      {mode.fare && (
                                        <p className="text-xs text-green-600 font-medium">Tarifa: {mode.fare}</p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Additional Information */}
                      {geolocationData.additional_info && (
                        <div className="space-y-4">
                          <h4 className="text-md font-medium text-slate-700">Informações Adicionais</h4>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-3 bg-blue-50 rounded-lg">
                              <h5 className="font-medium text-blue-800 mb-2">Horários de Funcionamento</h5>
                              <p className="text-sm text-blue-700">{geolocationData.additional_info.operating_hours}</p>
                            </div>

                            <div className="p-3 bg-green-50 rounded-lg">
                              <h5 className="font-medium text-green-800 mb-2">Custo Total Estimado</h5>
                              <p className="text-sm text-green-700">{geolocationData.additional_info.total_fare}</p>
                            </div>

                            <div className="p-3 bg-purple-50 rounded-lg">
                              <h5 className="font-medium text-purple-800 mb-2">Acessibilidade</h5>
                              <p className="text-sm text-purple-700">{geolocationData.additional_info.accessibility}</p>
                            </div>

                            <div className="p-3 bg-orange-50 rounded-lg">
                              <h5 className="font-medium text-orange-800 mb-2">Condições de Trânsito</h5>
                              <p className="text-sm text-orange-700">{geolocationData.additional_info.traffic_info}</p>
                              {geolocationData.additional_info.weather_warning && (
                                <p className="text-sm text-red-600 mt-1">⚠️ {geolocationData.additional_info.weather_warning}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Instructions */}
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <h4 className="text-md font-medium text-slate-700 mb-2">Como usar</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Preencha o <strong>endereço completo</strong> do funcionário na aba "Endereço"</li>
                      <li>• Configure o <strong>endereço padrão da empresa</strong> em "Cadastros → Endereços"</li>
                      <li>• Clique em <strong>"Calcular Rota"</strong> para obter informações de transporte público</li>
                      <li>• Use essas informações para auxiliar no planejamento de benefícios de transporte</li>
                    </ul>
                  </div>

                </CardContent>
              </Card>
            </TabsContent>
            {/* Escolaridade Tab */}
            <TabsContent value="escolaridade">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <GraduationCap className="h-5 w-5" />
                    <span>Escolaridade e Cursos</span>
                  </CardTitle>
                  <CardDescription>
                    Formação educacional e cursos extracurriculares do funcionário
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Nível de Escolaridade */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-slate-700 border-b pb-2">
                      Nível de Escolaridade
                    </h3>

                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="escolaridade_id">Selecione o Nível</Label>
                        <Select
                          value={formData.escolaridade_id}
                          onValueChange={(value) => handleInputChange('escolaridade_id', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o nível de escolaridade" />
                          </SelectTrigger>
                          <SelectContent>
                            {options.escolaridadeOptions.map((escolaridade) => (
                              <SelectItem key={escolaridade.id} value={escolaridade.id.toString()}>
                                {escolaridade.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {formData.escolaridade_id && (
                          <div className="mt-2 p-3 bg-blue-50 rounded-lg flex items-center space-x-2">
                            <Award className="h-5 w-5 text-blue-600" />
                            <span className="text-sm font-medium text-blue-900">
                              {options.escolaridadeOptions.find(esc => esc.id.toString() === formData.escolaridade_id)?.nome}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {options.escolaridadeOptions.length === 0 && (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center space-x-2 text-amber-800">
                          <AlertCircle className="h-5 w-5" />
                          <p className="text-sm">
                            Nenhum nível de escolaridade cadastrado. Acesse <strong>Cadastros → Escolaridade</strong> para criar.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Cursos Extracurriculares */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-2">
                      <h3 className="text-lg font-medium text-slate-700">
                        Cursos Extracurriculares
                      </h3>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          const cursos = formData.cursos || [];
                          setFormData({
                            ...formData,
                            cursos: [...cursos, '']
                          });
                        }}
                        className="btn-primary"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Adicionar Curso
                      </Button>
                    </div>

                    {formData.cursos && formData.cursos.length > 0 ? (
                      <div className="space-y-3">
                        {formData.cursos.map((curso, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <div className="flex-1">
                              <Input
                                value={curso}
                                onChange={(e) => {
                                  const newCursos = [...formData.cursos];
                                  newCursos[index] = e.target.value;
                                  setFormData({ ...formData, cursos: newCursos });
                                }}
                                placeholder={`Nome do curso ${index + 1}`}
                              />
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const newCursos = formData.cursos.filter((_, i) => i !== index);
                                setFormData({ ...formData, cursos: newCursos });
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                        <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500 mb-3">
                          Nenhum curso extracurricular adicionado
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              cursos: ['']
                            });
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Adicionar Primeiro Curso
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Dicas */}
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <h4 className="text-md font-medium text-slate-700 mb-2 flex items-center space-x-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <span>Exemplos de Cursos</span>
                    </h4>
                    <ul className="text-sm text-slate-600 space-y-1 ml-7">
                      <li>• Excel Avançado</li>
                      <li>• Inglês Intermediário</li>
                      <li>• Gestão de Projetos</li>
                      <li>• Marketing Digital</li>
                      <li>• Programação Python</li>
                    </ul>
                  </div>

                </CardContent>
              </Card>
            </TabsContent>
            {/* Grid Learning Tab */}
            <TabsContent value="lms">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <GraduationCap className="h-5 w-5 text-blue-600" />
                    <span>Grid Learning</span>
                  </CardTitle>
                  <CardDescription>
                    Gerencie os cursos atribuídos ao funcionário
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {!isEditing ? (
                    <div className="text-center py-8">
                      <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">Salve o funcionário primeiro para gerenciar cursos Grid Learning</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {lmsData.loading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="loading-spinner w-6 h-6 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
                          <span className="ml-3 text-gray-600">Carregando cursos...</span>
                        </div>
                      ) : (
                        <>
                          {/* Header with statistics */}
                          <div className="bg-blue-50 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="text-lg font-semibold text-blue-900">Resumo dos Cursos</h3>
                                <p className="text-blue-700 text-sm">
                                  {lmsData.assignedCourses.length} de {lmsData.allCourses.length} cursos atribuídos
                                </p>
                                {lmsData.assignedCourses.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    <p className="text-xs text-blue-600">
                                      {lmsData.assignedCourses.filter(c => c.status === 'completed').length} concluídos • {' '}
                                      {lmsData.assignedCourses.filter(c => c.progress_percentage > 0 && c.status !== 'completed').length} em progresso • {' '}
                                      {lmsData.assignedCourses.filter(c => c.progress_percentage === 0).length} não iniciados
                                    </p>
                                    <div className="w-32 bg-blue-200 rounded-full h-2">
                                      <div
                                        className="h-2 bg-blue-600 rounded-full"
                                        style={{
                                          width: `${lmsData.assignedCourses.length > 0 ?
                                            (lmsData.assignedCourses.reduce((sum, c) => sum + (c.progress_percentage || 0), 0) / lmsData.assignedCourses.length) : 0
                                            }%`
                                        }}
                                      ></div>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-blue-600">
                                  {lmsData.assignedCourses.length}
                                </div>
                                <div className="text-xs text-blue-600">Cursos Ativos</div>
                                {lmsData.assignedCourses.length > 0 && (
                                  <div className="text-sm font-medium text-blue-800 mt-1">
                                    {Math.round(lmsData.assignedCourses.reduce((sum, c) => sum + (c.progress_percentage || 0), 0) / lmsData.assignedCourses.length)}% concluído
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Course List */}
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h4 className="text-md font-semibold text-gray-800">
                                Catálogo de Cursos Disponíveis
                              </h4>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={fetchLMSData}
                                disabled={lmsData.loading}
                              >
                                Atualizar Lista
                              </Button>
                            </div>

                            {lmsData.allCourses.length === 0 ? (
                              <div className="text-center py-8">
                                <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500">Nenhum curso disponível no sistema</p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {lmsData.allCourses.map(course => {
                                  const assignedCourse = lmsData.assignedCourses.find(assigned => assigned.course_id === course.id);
                                  const isAssigned = !!assignedCourse;
                                  const progress = assignedCourse?.progress_percentage || 0;
                                  const status = assignedCourse?.status || 'active';

                                  return (
                                    <Card key={course.id} className={`transition-colors ${isAssigned ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
                                      <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center space-x-4 flex-1">
                                            <Checkbox
                                              id={`course-${course.id}`}
                                              checked={isAssigned}
                                              onCheckedChange={(checked) => handleLMSCourseToggle(course.id, !checked)}
                                            />

                                            <div className="flex-1">
                                              <div className="flex items-start justify-between">
                                                <div className="space-y-2 flex-1">
                                                  <label
                                                    htmlFor={`course-${course.id}`}
                                                    className="text-sm font-medium cursor-pointer hover:text-blue-600"
                                                  >
                                                    {course.title}
                                                  </label>
                                                  <p className="text-xs text-gray-600 line-clamp-2">
                                                    {course.short_description || course.description}
                                                  </p>

                                                  {/* Progress Bar - Only show for assigned courses */}
                                                  {isAssigned && (
                                                    <div className="space-y-2">
                                                      <div className="flex items-center justify-between text-xs">
                                                        <span className="text-gray-600">Progresso do curso</span>
                                                        <span className={`font-medium ${progress === 100 ? 'text-green-600' :
                                                          progress > 0 ? 'text-blue-600' : 'text-gray-500'
                                                          }`}>
                                                          {Math.round(progress)}% concluído
                                                        </span>
                                                      </div>
                                                      <div className="w-full bg-gray-200 rounded-full h-2">
                                                        <div
                                                          className={`h-2 rounded-full transition-all duration-300 ${progress === 100 ? 'bg-green-500' :
                                                            progress > 0 ? 'bg-blue-500' : 'bg-gray-300'
                                                            }`}
                                                          style={{ width: `${Math.max(progress, 2)}%` }}
                                                        ></div>
                                                      </div>
                                                      <div className="flex items-center justify-between text-xs">
                                                        <span className={`px-2 py-1 rounded ${status === 'completed' ? 'bg-green-100 text-green-800' :
                                                          progress > 0 ? 'bg-blue-100 text-blue-800' :
                                                            'bg-gray-100 text-gray-600'
                                                          }`}>
                                                          {status === 'completed' ? 'Concluído' :
                                                            progress > 0 ? 'Em Progresso' : 'Não Iniciado'}
                                                        </span>
                                                        {assignedCourse?.assigned_date && (
                                                          <span className="text-gray-500">
                                                            Atribuído em {new Date(assignedCourse.assigned_date).toLocaleDateString('pt-BR')}
                                                          </span>
                                                        )}
                                                      </div>
                                                    </div>
                                                  )}

                                                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                                                    {course.category && (
                                                      <span className="bg-gray-100 px-2 py-1 rounded">
                                                        {course.category}
                                                      </span>
                                                    )}

                                                    <div className="flex items-center space-x-1">
                                                      <Clock className="h-3 w-3" />
                                                      <span>
                                                        {course.duration_hours < 1
                                                          ? `${Math.round(course.duration_hours * 60)}min`
                                                          : `${course.duration_hours}h`
                                                        }
                                                      </span>
                                                    </div>

                                                    {course.level && (
                                                      <span className={`px-2 py-1 rounded text-xs ${course.level === 'beginner' ? 'bg-green-100 text-green-800' :
                                                        course.level === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                                                          'bg-red-100 text-red-800'
                                                        }`}>
                                                        {course.level === 'beginner' ? 'Iniciante' :
                                                          course.level === 'intermediate' ? 'Intermediário' : 'Avançado'}
                                                      </span>
                                                    )}

                                                    {course.is_mandatory && (
                                                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">
                                                        Obrigatório
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>

                                                <div className="ml-4">
                                                  {isAssigned ? (
                                                    <div className="flex flex-col items-center space-y-1">
                                                      <div className="flex items-center space-x-1 text-green-600">
                                                        <CheckCircle2 className="h-4 w-4" />
                                                        <span className="text-xs font-medium">Atribuído</span>
                                                      </div>
                                                      {progress === 100 && (
                                                        <div className="flex items-center space-x-1 text-green-600">
                                                          <Award className="h-3 w-3" />
                                                          <span className="text-xs">Certificado</span>
                                                        </div>
                                                      )}
                                                    </div>
                                                  ) : (
                                                    <div className="flex items-center space-x-1 text-red-600">
                                                      <CircleX className="h-4 w-4" />
                                                      <span className="text-xs font-medium">Não Atribuído</span>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </CardContent>
                                    </Card>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="border-t pt-4">
                            <div className="flex justify-between items-center">
                              <p className="text-sm text-gray-600">
                                Use os checkboxes acima para atribuir ou remover cursos deste funcionário.
                                As alterações são salvas automaticamente.
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={fetchLMSData}
                                disabled={lmsData.loading}
                              >
                                <GraduationCap className="h-4 w-4 mr-2" />
                                Atualizar
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            {/* Jornadas Tab */}
            <TabsContent value="jornadas">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Clock className="h-5 w-5 text-indigo-600" />
                    <span>Banco de Horas e Jornadas</span>
                  </CardTitle>
                  <CardDescription>
                    Configuração do acordo de banco de horas e jornada de trabalho para o funcionário
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">

                  {bancoHorasData.loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="flex items-center space-x-2 text-slate-600">
                        <div className="loading-spinner w-5 h-5 border-2 border-slate-200 border-t-slate-600 rounded-full"></div>
                        <span>Carregando dados de banco de horas...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">

                      {/* Acordo de Banco de Horas */}
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2 mb-4">
                          <Route className="h-5 w-5 text-blue-600" />
                          <h3 className="text-lg font-medium text-slate-700">Acordo de Banco de Horas</h3>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="acordo_banco_horas_id">Acordo de Banco de Horas</Label>
                            <Select
                              value={formData.acordo_banco_horas_id || ''}
                              onValueChange={(value) => handleInputChange('acordo_banco_horas_id', value === 'null' ? null : value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione um acordo de banco de horas" />
                              </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="null">Nenhum acordo selecionado</SelectItem>
                                {Array.isArray(bancoHorasData.acordos) && bancoHorasData.acordos.map((acordo) => (
                                  <SelectItem key={acordo.id} value={acordo.id}>
                                    {acordo.nome}
                                    {acordo.convencao_coletiva_nome ? ` - ${acordo.convencao_coletiva_nome}` : ''}
                                    {` (${acordo.periodo_compensacao_meses} meses)`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">
                              Selecione o acordo de banco de horas aplicável para este funcionário.
                            </p>
                          </div>

                          {/* Informações do acordo selecionado */}
                          {formData.acordo_banco_horas_id && bancoHorasData.acordos.length > 0 && (
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                              {(() => {
                                const acordoSelecionado = bancoHorasData.acordos.find(a => a.id === formData.acordo_banco_horas_id);
                                return acordoSelecionado ? (
                                  <div className="space-y-2">
                                    <h4 className="font-medium text-blue-800">Detalhes do Acordo Selecionado</h4>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                      <p><span className="font-medium">Nome:</span> {acordoSelecionado.nome}</p>
                                      <p><span className="font-medium">Convenção:</span> {acordoSelecionado.convencao_coletiva_nome || 'Não vinculada'}</p>
                                      <p><span className="font-medium">Período:</span> {acordoSelecionado.periodo_compensacao_meses} meses</p>
                                      <p><span className="font-medium">Limite Mensal:</span> {acordoSelecionado.limite_mensal_horas ? `${acordoSelecionado.limite_mensal_horas}h` : 'Não definido'}</p>
                                      <p><span className="font-medium">Compensação:</span> {acordoSelecionado.compensacao_tipo === 'mensal' ? 'Mensal' : 'Fim do período'}</p>
                                      <p><span className="font-medium">Ativo:</span> {acordoSelecionado.ativo ? 'Sim' : 'Não'}</p>
                                    </div>
                                    {acordoSelecionado.descricao && (
                                      <div className="mt-2">
                                        <p className="font-medium text-blue-800">Descrição:</p>
                                        <p className="text-sm text-blue-700">{acordoSelecionado.descricao}</p>
                                      </div>
                                    )}
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Jornada de Trabalho */}
                      <div className="space-y-4 border-t pt-6">
                        <div className="flex items-center space-x-2 mb-4">
                          <Clock className="h-5 w-5 text-green-600" />
                          <h3 className="text-lg font-medium text-slate-700">Jornada de Trabalho</h3>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="jornada_id">Jornada de Trabalho</Label>
                            <Select
                              value={formData.jornada_id || ''}
                              onValueChange={(value) => handleInputChange('jornada_id', value === 'null' ? null : value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione uma jornada de trabalho" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="null">Nenhuma jornada selecionada</SelectItem>
                                {Array.isArray(bancoHorasData.jornadas) && bancoHorasData.jornadas.map((jornada) => (
                                  <SelectItem key={jornada.id} value={jornada.id}>
                                    {jornada.nome} - {jornada.horas_diarias}h/dia, {jornada.dias_semana} dias
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">
                              Defina a jornada de trabalho padrão para calcular horas extras e banco de horas.
                            </p>
                          </div>

                          {/* Informações detalhadas da jornada selecionada */}
                          {formData.jornada_id && bancoHorasData.jornadas.length > 0 && (
                            <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                              {(() => {
                                const jornadaSelecionada = bancoHorasData.jornadas.find(j => j.id === formData.jornada_id);
                                if (!jornadaSelecionada) return null;

                                return (
                                  <div className="space-y-3">
                                    <h4 className="font-medium text-green-800 flex items-center">
                                      <Clock className="h-4 w-4 mr-2" />
                                      Detalhes da Jornada Selecionada
                                    </h4>

                                    {/* Informações Básicas */}
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                      <div>
                                        <span className="font-medium text-green-800">Nome:</span>
                                        <p className="text-green-700">{jornadaSelecionada.nome}</p>
                                      </div>
                                      <div>
                                        <span className="font-medium text-green-800">Jornada Semanal:</span>
                                        <p className="text-green-700">{jornadaSelecionada.jornada_semanal || (jornadaSelecionada.horas_diarias * (jornadaSelecionada.dias_semana?.length || 5))}h</p>
                                      </div>
                                    </div>

                                    {jornadaSelecionada.descricao && (
                                      <div className="text-sm">
                                        <span className="font-medium text-green-800">Descrição:</span>
                                        <p className="text-green-700">{jornadaSelecionada.descricao}</p>
                                      </div>
                                    )}

                                    {/* Horários de Trabalho */}
                                    {jornadaSelecionada.horarios && jornadaSelecionada.horarios.length > 0 && (
                                      <div className="border-t border-green-300 pt-3">
                                        <p className="font-medium text-green-800 mb-2 text-sm">Horários:</p>
                                        <div className="space-y-2">
                                          {jornadaSelecionada.horarios.map((horario, idx) => (
                                            <div key={idx} className="bg-white/50 p-2 rounded text-xs">
                                              <p className="font-medium text-green-800">{horario.nome}</p>
                                              <p className="text-green-700">
                                                {horario.entrada} às {horario.saida}
                                                {horario.intervalo_inicio && horario.intervalo_fim &&
                                                  ` • Intervalo: ${horario.intervalo_inicio}-${horario.intervalo_fim}`
                                                }
                                              </p>
                                              <p className="text-green-600 font-medium">
                                                {horario.horas_calculadas ||
                                                  (() => {
                                                    const calcHoras = (e, s, ii, if_) => {
                                                      try {
                                                        const [eh, em] = e.split(':').map(Number);
                                                        const [sh, sm] = s.split(':').map(Number);
                                                        let total = (sh * 60 + sm) - (eh * 60 + em);
                                                        if (ii && if_) {
                                                          const [iih, iim] = ii.split(':').map(Number);
                                                          const [ifh, ifm] = if_.split(':').map(Number);
                                                          total -= ((ifh * 60 + ifm) - (iih * 60 + iim));
                                                        }
                                                        return (total / 60).toFixed(1);
                                                      } catch { return '0'; }
                                                    };
                                                    return calcHoras(horario.entrada, horario.saida, horario.intervalo_inicio, horario.intervalo_fim);
                                                  })()
                                                }h por dia
                                              </p>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Dias da Semana */}
                                    {jornadaSelecionada.dias_horarios && jornadaSelecionada.dias_horarios.length > 0 ? (
                                      <div className="border-t border-green-300 pt-3">
                                        <p className="font-medium text-green-800 mb-2 text-sm">Dias da Semana:</p>
                                        <div className="flex flex-wrap gap-1">
                                          {jornadaSelecionada.dias_horarios
                                            .filter(d => d.horario_id)
                                            .map((diaMap, idx) => {
                                              const diasNomes = {
                                                segunda: 'Seg', terca: 'Ter', quarta: 'Qua',
                                                quinta: 'Qui', sexta: 'Sex', sabado: 'Sáb', domingo: 'Dom'
                                              };
                                              const horario = jornadaSelecionada.horarios?.find(h => h.id === diaMap.horario_id);
                                              return (
                                                <span key={idx} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                                                  {diasNomes[diaMap.dia]} ({horario?.nome || 'N/A'})
                                                </span>
                                              );
                                            })}
                                        </div>
                                      </div>
                                    ) : jornadaSelecionada.dias_semana && jornadaSelecionada.dias_semana.length > 0 && (
                                      <div className="border-t border-green-300 pt-3">
                                        <p className="font-medium text-green-800 mb-2 text-sm">Dias da Semana (Estrutura Antiga):</p>
                                        <div className="flex flex-wrap gap-1">
                                          {jornadaSelecionada.dias_semana.map((dia, idx) => {
                                            const diasNomes = {
                                              segunda: 'Segunda', terca: 'Terça', quarta: 'Quarta',
                                              quinta: 'Quinta', sexta: 'Sexta', sabado: 'Sábado', domingo: 'Domingo'
                                            };
                                            return (
                                              <span key={idx} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                                                {diasNomes[dia] || dia}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Modalidade de Ponto Individual */}
                      <div className="space-y-4 border-t pt-6">
                        <div className="flex items-center space-x-2 mb-4">
                          <Smartphone className="h-5 w-5 text-indigo-600" />
                          <h3 className="text-lg font-medium text-slate-700">Modalidade de Ponto Individual</h3>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                          <div className="space-y-2">
                            <Select
                              value={formData.modalidade_ponto || 'inherit_company'}
                              onValueChange={(value) => handleInputChange('modalidade_ponto', value === 'inherit_company' ? '' : value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione a modalidade de ponto" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="inherit_company">Seguir padrão da empresa</SelectItem>
                                <SelectItem value="PIN">PIN</SelectItem>
                                <SelectItem value="APP">Aplicativo</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">
                              Define a modalidade de registro de ponto deste funcionário. Deixe em "Seguir padrão da empresa" para herdar a configuração da empresa.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Contribuição Sindical */}
                      <div className="space-y-4 border-t pt-6">
                        <div className="flex items-center justify-between rounded-lg border p-4">
                          <div>
                            <h3 className="text-lg font-medium text-slate-700">Contribuição Sindical</h3>
                            <p className="text-xs text-slate-500 mt-1">
                              Defina se o colaborador é optante do sindicato. Quando desativado, a folha não lança a contribuição sindical automática.
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-sm font-medium ${formData.optante_sindicato ? 'text-emerald-700' : 'text-slate-500'}`}>
                              {formData.optante_sindicato ? 'Optante' : 'Não optante'}
                            </span>
                            <Switch
                              checked={!!formData.optante_sindicato}
                              onCheckedChange={(value) => handleInputChange('optante_sindicato', value)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Geofences (Sistema de Ponto) */}
                      <div className="space-y-4 border-t pt-6">
                        <div className="flex items-center space-x-2 mb-4">
                          <MapPin className="h-5 w-5 text-purple-600" />
                          <h3 className="text-lg font-medium text-slate-700">Geofences (Sistema de Ponto)</h3>
                          {formData.regime === 'CLT' && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                              * Obrigatório para CLT
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                          {geofencesData.loading ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="flex items-center space-x-2 text-slate-600">
                                <div className="loading-spinner w-5 h-5 border-2 border-slate-200 border-t-slate-600 rounded-full"></div>
                                <span>Carregando geofences...</span>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="space-y-2">
                                <Label>Geofences Vinculadas {formData.regime === 'CLT' && <span className="text-red-500">*</span>}</Label>
                                <div className="space-y-2">
                                  {geofencesData.geofences.length === 0 ? (
                                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-700">
                                      <p className="font-medium">⚠️ Nenhuma geofence cadastrada</p>
                                      <p className="mt-1">Configure geofences no menu Admin - Ponto antes de vincular ao colaborador.</p>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {geofencesData.geofences.map((geofence) => (
                                          <div
                                            key={geofence.id}
                                            className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${formData.geofence_ids.includes(geofence.id)
                                              ? 'border-purple-500 bg-purple-50'
                                              : 'border-gray-200 hover:border-purple-300'
                                              }`}
                                            onClick={() => {
                                              const isSelected = formData.geofence_ids.includes(geofence.id);
                                              if (isSelected) {
                                                // Remove geofence
                                                handleInputChange(
                                                  'geofence_ids',
                                                  formData.geofence_ids.filter(id => id !== geofence.id)
                                                );
                                              } else {
                                                // Add geofence
                                                handleInputChange(
                                                  'geofence_ids',
                                                  [...formData.geofence_ids, geofence.id]
                                                );
                                              }
                                            }}
                                          >
                                            <div className="flex items-center space-x-2">
                                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${formData.geofence_ids.includes(geofence.id)
                                                ? 'border-purple-500 bg-purple-500'
                                                : 'border-gray-300'
                                                }`}>
                                                {formData.geofence_ids.includes(geofence.id) && (
                                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                  </svg>
                                                )}
                                              </div>
                                              <div className="flex-1">
                                                <p className="font-medium text-sm text-slate-700">{geofence.nome}</p>
                                                <p className="text-xs text-slate-500">Raio: {geofence.raio_metros}m</p>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>

                                      {/* Selected geofences chips */}
                                      {formData.geofence_ids.length > 0 && (
                                        <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-md">
                                          <p className="text-sm font-medium text-purple-800 mb-2">
                                            {formData.geofence_ids.length} Geofence{formData.geofence_ids.length > 1 ? 's' : ''} Selecionada{formData.geofence_ids.length > 1 ? 's' : ''}:
                                          </p>
                                          <div className="flex flex-wrap gap-2">
                                            {formData.geofence_ids.map((geoId) => {
                                              const geo = geofencesData.geofences.find(g => g.id === geoId);
                                              return geo ? (
                                                <span
                                                  key={geoId}
                                                  className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                                                >
                                                  <MapPin className="h-3 w-3 mr-1" />
                                                  {geo.nome}
                                                  <button
                                                    type="button"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleInputChange(
                                                        'geofence_ids',
                                                        formData.geofence_ids.filter(id => id !== geoId)
                                                      );
                                                    }}
                                                    className="ml-2 hover:text-purple-900"
                                                  >
                                                    ×
                                                  </button>
                                                </span>
                                              ) : null;
                                            })}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                                <p className="text-xs text-slate-500">
                                  {formData.regime === 'CLT'
                                    ? 'Colaboradores CLT devem ter pelo menos uma geofence vinculada para validação de ponto.'
                                    : 'Selecione as geofences onde o colaborador poderá registrar ponto.'}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Seção "Horários de Trabalho" removida - Funcionalidade integrada em "Jornadas" */}

                      {/* Informações e Alertas */}
                      <div className="space-y-4 border-t pt-6">
                        <div className="flex items-center space-x-2 mb-4">
                          <AlertCircle className="h-5 w-5 text-orange-600" />
                          <h3 className="text-lg font-medium text-slate-700">Informações Importantes</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                            <h4 className="font-medium text-yellow-800 mb-2">⚠️ Banco de Horas</h4>
                            <ul className="text-sm text-yellow-700 space-y-1">
                              <li>• Acordo de banco de horas define os limites legais</li>
                              <li>• Período de compensação pode ser 4, 6 ou 12 meses</li>
                              <li>• Horas extras são automaticamente convertidas</li>
                              <li>• Saldo é calculado com base nos registros de ponto</li>
                            </ul>
                          </div>

                          <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-md">
                            <h4 className="font-medium text-indigo-800 mb-2">🕐 Jornada de Trabalho</h4>
                            <ul className="text-sm text-indigo-700 space-y-1">
                              <li>• Define as horas padrão de trabalho</li>
                              <li>• Base para cálculo de horas extras</li>
                              <li>• Pode variar por cargo ou função</li>
                              <li>• Integrada ao sistema REP-P</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="border-t pt-4">
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-gray-600">
                            Configure o acordo de banco de horas e jornada para automatizar cálculos de ponto eletrônico.
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={fetchBancoHorasData}
                            disabled={bancoHorasData.loading}
                          >
                            <Clock className="h-4 w-4 mr-2" />
                            Atualizar
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            {/* Feedback Tab */}
            <TabsContent value="feedback">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Gift className="h-5 w-5 text-purple-600" />
                    <span>Histórico de Feedback</span>
                  </CardTitle>
                  <CardDescription>
                    Visualize todos os feedbacks dados e recebidos por este funcionário
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <FeedbackHistory employeeId={formData.id} employeeName={formData.nome} />
                </CardContent>
              </Card>
            </TabsContent>
            {/* DCF Tab */}
            <TabsContent value="dcf">
              <DCFEmployeeTab
                employeeId={formData.id}
                employeeName={formData.nome}
                cargoId={formData.cargo_id}
                departamentoId={formData.departamento_id}
              />
            </TabsContent>
            {/* Acesso Ponto Tab */}
            <TabsContent value="ponto">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Smartphone className="h-5 w-5 text-emerald-600" />
                    <span>Acesso ao Ponto-App</span>
                  </CardTitle>
                  <CardDescription>
                    Gerencie o PIN de acesso do colaborador ao aplicativo de ponto eletrônico
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {!isEditing ? (
                    <div className="text-center py-8">
                      <Smartphone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">Salve o funcionário primeiro para gerenciar o PIN de acesso</p>
                    </div>
                  ) : pinData.loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className={`p-3 rounded-full ${pinData.tem_pin ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                              <Key className={`h-6 w-6 ${pinData.tem_pin ? 'text-emerald-600' : 'text-slate-400'}`} />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-700">Status do PIN</p>
                              <div className="flex items-center space-x-2 mt-1">
                                {pinData.tem_pin ? (
                                  <>
                                    <Check className="h-4 w-4 text-emerald-600" />
                                    <span className="text-emerald-700 font-medium text-sm">
                                      {pinData.pin ? 'PIN disponível para visualização' : 'PIN legado cadastrado'}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <X className="h-4 w-4 text-slate-400" />
                                    <span className="text-slate-500 font-medium text-sm">Nenhum PIN cadastrado</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          {pinData.tem_pin ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={resetPin}
                              disabled={pinData.generating}
                              className="text-amber-600 border-amber-300 hover:bg-amber-50"
                            >
                              {pinData.generating ? (
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <Key className="h-4 w-4 mr-1" />
                              )}
                              Redefinir PIN
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              size="sm"
                              onClick={gerarPin}
                              disabled={pinData.generating}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              {pinData.generating ? (
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <Key className="h-4 w-4 mr-1" />
                              )}
                              Gerar PIN
                            </Button>
                          )}
                        </div>
                      </div>

                      {pinData.tem_pin && (
                        <div className={`p-4 rounded-lg border ${pinData.pin ? 'bg-slate-50 border-slate-200' : 'bg-amber-50 border-amber-200'}`}>
                          <p className="text-xs font-medium text-slate-500 mb-2">PIN atual</p>
                          {pinData.pin ? (
                            <div className="text-3xl font-mono font-bold tracking-widest text-slate-800">
                              {pinData.pin}
                            </div>
                          ) : (
                            <p className="text-sm text-amber-700">
                              PIN legado detectado. Gere um novo PIN para permitir visualização futura no Grid.
                            </p>
                          )}
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <h4 className="font-medium text-blue-800 mb-2 flex items-center">
                            <Smartphone className="h-4 w-4 mr-2" />
                            Como funciona?
                          </h4>
                          <ul className="text-sm text-blue-700 space-y-1">
                            <li>• O colaborador usa o PIN para acessar o Ponto-App</li>
                            <li>• O PIN é numérico de 6 dígitos</li>
                            <li>• O PIN pode ser gerado automaticamente pelo sistema</li>
                            <li>• Anote o PIN e entregue ao colaborador de forma segura</li>
                          </ul>
                        </div>
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <h4 className="font-medium text-amber-800 mb-2 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-2" />
                            Importante
                          </h4>
                          <ul className="text-sm text-amber-700 space-y-1">
                            <li>• O PIN pode ser consultado depois no Grid por administradores e pelo próprio colaborador</li>
                            <li>• Em caso de perda, um novo PIN deve ser gerado</li>
                            <li>• O PIN antigo é automaticamente invalidado</li>
                            <li>• Para gerenciar PINs em lote, use o menu Admin → PINs</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* PIN Dialog */}
                  <Dialog open={!!pinData.showDialog} onOpenChange={(o) => !o && setPinData(prev => ({ ...prev, showDialog: null }))}>
                    <DialogContent className="max-w-sm">
                      <DialogHeader>
                        <DialogTitle>PIN Gerado</DialogTitle>
                        <DialogDescription>
                          Entregue este PIN ao colaborador. A visualização posterior fica restrita ao Grid.
                        </DialogDescription>
                      </DialogHeader>
                      {pinData.showDialog && (
                        <div className="text-center py-6">
                          <p className="text-sm text-slate-500 mb-2">{pinData.showDialog.nome}</p>
                          <div className="text-4xl font-mono font-bold tracking-widest text-primary bg-slate-100 rounded-lg py-4 px-6 inline-block">
                            {pinData.showDialog.pin}
                          </div>
                        </div>
                      )}
                      <DialogFooter>
                        <Button onClick={() => setPinData(prev => ({ ...prev, showDialog: null }))} className="w-full">
                          Anotei o PIN
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            </TabsContent>
              </>
            )}
          </div>
        </Tabs>

        <div className="flex justify-end space-x-4 pt-6 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(returnPath)}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading || isInitialDataLoading}
            className="btn-primary"
          >
            {loading ? 'Salvando...' : (isEditing ? 'Atualizar Funcionário' : 'Salvar Funcionário')}
          </Button>
        </div>
      </form>
    </div >
  );
};

export default DetailedEmployeeForm;
